import {
	clarificationFromOnboardingResultError,
	projectOnboardingFirstResultClarification,
	readOnboardingFirstResultClarification,
	type OnboardingFirstResultClarification
} from '$lib/aeoll/chat/onboardingFirstResultClarificationPolicy';
import { sha256 } from 'js-sha256';
import type { OnboardingCommandAdmissionEvidence } from '$lib/aeoll/chat/onboardingCommandExecutionController';
import { onboardingErrorText } from '$lib/aeoll/chat/onboardingErrorText';
import {
	ONBOARDING_FIRST_RESULT_REVISION_CONFLICT_MESSAGE,
	isOnboardingFirstResultRevisionConflict,
	normalizeOnboardingRevision,
	submitOnboardingFirstResultSelection
} from '$lib/aeoll/onboarding/firstResultSelectionSubmission';
import {
	addOnboardingLinkSources,
	addOnboardingTextSource,
	analyzeOnboardingOnAPILinks,
	answerOnboardingBrandDiscoveryQuestion,
	applyOnboardingBrandDiscovery,
	completeOnboarding,
	createOnboardingFirstDrafts,
	getOnboardingBrandDiscovery,
	getOnboardingBrandDiscoveryFindings,
	getOnboardingBrandDiscoveryQuestions,
	getOnboardingBrandVoiceCalibration,
	getOnboardingState,
	getOnboardingCalibrationContinuation,
	continueOnboardingCalibration,
	selectOnboardingFirstResult,
	startOnboarding,
	startOnboardingBrandDiscovery,
	type BrandDiscoveryFinding,
	type BrandDiscoveryQuestion,
	type BrandDiscoveryRun,
	type BrandVoiceCalibrationAuthorityV1,
	type FirstResultType,
	type FirstDraftsResponse
} from '$lib/apis/content-factory/onboarding';
import {
	projectOnboardingDiscoveryQuestionContinuation,
	sameOnboardingDiscoveryQuestion,
	type OnboardingDiscoveryQuestionContinuationV1
} from '$lib/aeoll/chat/onboardingDiscoveryQuestionContinuation';
import { projectScopedOnboardingPendingQuestion } from '$lib/aeoll/chat/onboardingPendingQuestionAuthority';
import {
	projectPendingOnboardingQuestionComposer,
	type PendingOnboardingComposerFlow
} from '$lib/aeoll/chat/onboardingPendingQuestionComposer';
import {
	assertOnboardingCalibrationContinuation,
	OnboardingCalibrationPendingError,
	OnboardingAcceptedAnswerContinuationError
} from '$lib/aeoll/chat/onboardingCalibrationContinuation';
import { executeUnknownCalibrationRecovery } from '$lib/aeoll/chat/onboardingCalibrationRecoveryCommand';
import {
	getOnboardingHttpStatus,
	isOnboardingUnauthorizedError,
	runOnboardingLiveAuthStep,
	toOnboardingLiveAuthSupplier,
	type OnboardingLiveAuthSource
} from '$lib/aeoll/chat/onboardingLiveAuth';
import {
	extractOnboardingSourceLinks,
	removeOnboardingSourceLinks
} from '$lib/aeoll/onboarding/onboardingSourceReferencePolicy';
import { assertOnboarding1LinkImportSucceeded } from '$lib/aeoll/onboarding/onboardingSourceImportFailurePolicy';
import { createContentFactorySource } from '$lib/apis/content-factory';
import {
	applyBrandVoiceRun,
	listBrandVoiceFindings,
	reviewBrandVoiceFindingsBatch,
	type BrandVoiceFinding
} from '$lib/apis/content-factory/brandVoice';
import { resolveContentFactoryTargetPlatforms } from '../contentFactoryPlatformIntent';
import { buildContentFactoryInternalTask } from '../contentFactoryInternalTask';
import type { Onboarding1InfoCard } from './onboarding1Actions';
import {
	isOnboarding1PresentationCorrectionClause,
	isOnboarding1OfferingCorrectionClause,
	normalizeOnboarding1ReviewText,
	resolveOnboarding1AffirmativeChoice,
	resolveOnboarding1ChoiceCorrection,
	type Onboarding1ChoiceCorrection
} from './onboarding1CorrectionIntentPolicy';
import { sanitizeOnboardingRuntimeMarker } from '$lib/aeoll/onboarding/onboardingRuntimeMarkerPolicy';

export {
	assertOnboarding1LinkImportSucceeded,
	Onboarding1SourceImportError
} from '$lib/aeoll/onboarding/onboardingSourceImportFailurePolicy';

export const onboarding1ActiveChatStorageKey = 'open-webui:onboarding1:active-chat-id';
export const getOnboarding1ActiveChatStorageKey = (userId = '') => {
	const normalizedUserId = userId.trim();
	return normalizedUserId
		? `${onboarding1ActiveChatStorageKey}:user:${encodeURIComponent(normalizedUserId)}`
		: onboarding1ActiveChatStorageKey;
};
export const onboarding1StateStorageKey = 'contentFactoryOnboardingStateId';
export const onboarding1SelectedBrandStorageKey = 'content-factory-agent-brand-id';
export const getOnboarding1SelectedBrandStorageKey = (userId: unknown): string | null => {
	const normalizedUserId = typeof userId === 'string' ? userId.trim() : '';
	return normalizedUserId
		? `${onboarding1SelectedBrandStorageKey}:user:${encodeURIComponent(normalizedUserId)}`
		: null;
};
export const getOnboarding1FlowStorageKey = (chatId: string) =>
	`open-webui:onboarding1:flow:${chatId}`;

type Onboarding1SelectedBrandStorage = {
	getItem: (key: string) => string | null;
	removeItem: (key: string) => void;
	setItem: (key: string, value: string) => void;
};

export const readOnboarding1SelectedBrand = (
	storage: Onboarding1SelectedBrandStorage | null | undefined,
	userId: unknown
): string | null => {
	const key = getOnboarding1SelectedBrandStorageKey(userId);
	return storage && key ? storage.getItem(key) : null;
};

export const writeOnboarding1SelectedBrand = (
	storage: Onboarding1SelectedBrandStorage | null | undefined,
	userId: unknown,
	brandId: unknown
): boolean => {
	const key = getOnboarding1SelectedBrandStorageKey(userId);
	const normalizedBrandId = typeof brandId === 'string' ? brandId.trim() : '';
	if (!storage || !key || !normalizedBrandId) return false;
	storage.removeItem(onboarding1SelectedBrandStorageKey);
	storage.setItem(key, normalizedBrandId);
	return true;
};

export const clearOnboarding1SelectedBrand = (
	storage: Onboarding1SelectedBrandStorage | null | undefined,
	userId: unknown
): void => {
	if (!storage) return;
	const key = getOnboarding1SelectedBrandStorageKey(userId);
	if (key) storage.removeItem(key);
	storage.removeItem(onboarding1SelectedBrandStorageKey);
};

export type Onboarding1FlowStage =
	| 'idle'
	| 'awaiting_direction'
	| 'awaiting_source_choice'
	| 'awaiting_source'
	| 'awaiting_brand_seed'
	| 'analyzing_memory'
	| 'awaiting_discovery_question'
	| 'awaiting_fact_confirmation'
	| 'awaiting_brand_voice'
	| 'applying_memory'
	| 'memory_review'
	| 'awaiting_memory_correction'
	| 'awaiting_series_count'
	| 'awaiting_topic_choice'
	| 'awaiting_topic'
	| 'awaiting_task'
	| 'generating_first_post'
	| 'generating_result'
	| 'completed'
	| 'failed';

const ONBOARDING1_FILE_UPLOAD_STAGES: ReadonlySet<Onboarding1FlowStage> = new Set([
	'idle',
	'awaiting_direction',
	'awaiting_source_choice',
	'awaiting_source',
	'awaiting_brand_seed',
	'awaiting_memory_correction'
]);

export const isOnboarding1FileUploadAllowed = (
	stage: string,
	flow?: PendingOnboardingComposerFlow
) => {
	if (stage === 'awaiting_discovery_question') {
		return (
			flow?.stage === stage &&
			projectPendingOnboardingQuestionComposer(flow)?.status === 'ready' &&
			flow.pendingDiscoveryQuestion?.allowCustomAnswer === true &&
			flow.pendingDiscoveryQuestion.options.length === 0
		);
	}
	return ONBOARDING1_FILE_UPLOAD_STAGES.has(stage as Onboarding1FlowStage);
};

export type Onboarding1BrandSummary = {
	style: string;
	themes: string;
	delivery: string;
	audience: string;
	evidence: string;
	offering?: string;
	customerJob?: string;
	trustSignals?: string;
	contentGoal?: string;
	publishingCadence?: string;
	workflowContext?: string;
	targetPlatforms?: string[];
	sourceLabel?: string;
	sourceUrl?: string;
	audienceNote?: string;
	evidenceItems?: string[];
	calibrationVariants?: Onboarding1CalibrationVariant[];
	calibrationChoiceSetRef?: string;
};

export type Onboarding1CalibrationVariantId = 'variant_1' | 'variant_2';

export type Onboarding1CalibrationVariant = {
	id: Onboarding1CalibrationVariantId;
	title: string;
	body: string;
	basis: string;
	profileSummary: string;
	tone: string[];
	choiceRef?: string;
};

export type Onboarding1ReferenceScope = '' | 'style_or_structure';

export type Onboarding1FlowState = {
	version: 1;
	stage: Onboarding1FlowStage;
	brandReady: boolean;
	intentId: string;
	actionId: string;
	cardId: string;
	modeLabel: string;
	composerPlaceholder: string;
	stateId: string;
	workspaceId: string;
	brandId: string;
	/** Server revision captured with the actionable onboarding projection. */
	onboardingRevision: number | null;
	importSessionId: string;
	sourceIds: string[];
	/** Business/source material used as evidence for Brand Voice analysis. */
	analysisSourceIds: string[];
	/** Trusted user corrections that steer presentation without becoming business evidence. */
	brandVoiceGuidance: string[];
	discoveryRunId: string;
	brandVoiceRunId: string;
	referenceScope: Onboarding1ReferenceScope;
	sourceMode: '' | 'existing' | 'scratch' | 'universal';
	/** True after the user explicitly chose to continue without external source material. */
	sourceReferenceDeclined: boolean;
	topic: string;
	sourceContent: string;
	requestedCount: number;
	correctionCount: number;
	/** Which review the current free-form correction belongs to. */
	correctionTarget: '' | 'facts' | 'brand_voice';
	/** Explicit ownership of the currently visible Brand Voice review. */
	brandMemoryAnalysisStatus: 'ready' | 'running';
	/** Stable materialization attempt. Ambiguous retries keep it; explicit failures advance it. */
	firstResultAttempt: number;
	firstResultClarification?: OnboardingFirstResultClarification | null;
	summary: Onboarding1BrandSummary | null;
	selectedCalibrationId: Onboarding1CalibrationVariantId | '';
	selectedCalibrationChoiceRef: string;
	/** Stable assistant message that originally asked the user to confirm the facts. */
	factsReviewMessageId: string;
	brandVoiceReviewMessageId: string;
	/** Ephemeral display projection only; mutations re-enter the server-owned Reasoned state. */
	pendingDiscoveryQuestion: OnboardingDiscoveryQuestionContinuationV1 | null;
	/** Explicit server-admitted action retained across first-result continuation. */
	admissionEvidence?: OnboardingCommandAdmissionEvidence | null;
};

export const buildOnboarding1ChatTitle = ({
	flow,
	previewTitle = '',
	prompt = ''
}: {
	flow: Onboarding1FlowState;
	previewTitle?: string;
	prompt?: string;
}) => {
	const candidate = String(
		previewTitle || flow.summary?.offering || flow.topic || prompt || 'Новый чат'
	)
		.replace(/https?:\/\/\S+/gi, ' ')
		.replace(/[*_#`]/g, '')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/[.!?;,—:-]+$/g, '');

	if (candidate.length <= 52) return candidate || 'Новый чат';
	const shortened = candidate
		.slice(0, 52)
		.replace(/\s+\S*$/, '')
		.trim();
	return `${shortened || candidate.slice(0, 52).trim()}…`;
};

export type Onboarding1AssistantContext = {
	promptVersion: 1;
	surface: 'onboarding1' | 'content_factory_chat';
	flowStage: Onboarding1FlowStage;
	brandReady: boolean;
	publishingContext: {
		stateKnown: boolean;
		connectedChannelCount: number;
		platforms: string[];
	};
	intentId: string;
	hasTaskBrief: boolean;
	taskState: {
		hasTopic: boolean;
		hasSourceContent: boolean;
		requestedCount: number | null;
		sourceMode: Onboarding1FlowState['sourceMode'] | null;
	};
	selectedMode: {
		actionId: string;
		cardId: string;
		label: string;
	} | null;
	openedActionIndex: number | null;
};

export const buildOnboarding1AssistantContext = ({
	flow,
	surface,
	openedActionIndex = null,
	publishingContext = {
		stateKnown: false,
		connectedChannelCount: 0,
		platforms: []
	}
}: {
	flow: Onboarding1FlowState;
	surface: Onboarding1AssistantContext['surface'];
	openedActionIndex?: number | null;
	publishingContext?: Onboarding1AssistantContext['publishingContext'];
}): Onboarding1AssistantContext => ({
	promptVersion: 1,
	surface,
	flowStage: flow.stage,
	brandReady: flow.brandReady,
	publishingContext: {
		stateKnown: Boolean(publishingContext.stateKnown),
		connectedChannelCount: Math.max(0, Number(publishingContext.connectedChannelCount) || 0),
		platforms: Array.from(
			new Set(
				publishingContext.platforms.map((platform) => platform.trim().toLowerCase()).filter(Boolean)
			)
		)
	},
	intentId: flow.intentId,
	hasTaskBrief: Boolean(flow.topic.trim() || flow.sourceContent.trim()),
	taskState: {
		hasTopic: Boolean(flow.topic.trim()),
		hasSourceContent: Boolean(flow.sourceContent.trim()),
		requestedCount: flow.requestedCount > 0 ? flow.requestedCount : null,
		sourceMode: flow.sourceMode || null
	},
	selectedMode:
		flow.actionId && flow.cardId
			? {
					actionId: flow.actionId,
					cardId: flow.cardId,
					label: flow.modeLabel
				}
			: null,
	openedActionIndex
});

export type Onboarding1ComposerDecision = {
	id: string;
	message: string;
	confirmLabel: string;
	declineLabel?: string;
	alternateLabel?: string;
};

export const buildOnboarding1ComposerDecisionOptions = (
	decision: Onboarding1ComposerDecision
): Array<{ id: 'accept' | 'decline' | 'alternate'; label: string }> => [
	{ id: 'accept', label: decision.confirmLabel },
	...(decision.declineLabel ? [{ id: 'decline' as const, label: decision.declineLabel }] : []),
	...(decision.alternateLabel ? [{ id: 'alternate' as const, label: decision.alternateLabel }] : [])
];

export const createOnboarding1FlowState = (
	overrides: Partial<Onboarding1FlowState> = {}
): Onboarding1FlowState => ({
	version: 1,
	stage: 'idle',
	brandReady: false,
	intentId: '',
	actionId: '',
	cardId: '',
	modeLabel: '',
	composerPlaceholder: '',
	stateId: '',
	workspaceId: '',
	brandId: '',
	onboardingRevision: null,
	importSessionId: '',
	sourceIds: [],
	analysisSourceIds: [],
	brandVoiceGuidance: [],
	discoveryRunId: '',
	brandVoiceRunId: '',
	referenceScope: '',
	sourceMode: '',
	sourceReferenceDeclined: false,
	topic: '',
	sourceContent: '',
	requestedCount: 0,
	correctionCount: 0,
	correctionTarget: '',
	brandMemoryAnalysisStatus: 'ready',
	firstResultAttempt: 1,
	summary: null,
	selectedCalibrationId: '',
	selectedCalibrationChoiceRef: '',
	factsReviewMessageId: '',
	brandVoiceReviewMessageId: '',
	pendingDiscoveryQuestion: null,
	admissionEvidence: null,
	...overrides
});

export const persistOnboarding1SelectedBrand = (
	storage: Onboarding1SelectedBrandStorage | null | undefined,
	flow: Onboarding1FlowState,
	userId: unknown
): boolean => {
	if (!flow.brandReady) return false;
	return writeOnboarding1SelectedBrand(storage, userId, flow.brandId);
};

export const restoreOnboarding1FlowState = (value: unknown): Onboarding1FlowState => {
	if (!value || typeof value !== 'object') return createOnboarding1FlowState();
	const candidate = value as Partial<Onboarding1FlowState>;
	if (candidate.version !== 1) return createOnboarding1FlowState();
	const restored = createOnboarding1FlowState({
		...candidate,
		sourceIds: Array.isArray(candidate.sourceIds)
			? candidate.sourceIds.map(String).filter(Boolean)
			: [],
		analysisSourceIds: Array.isArray(candidate.analysisSourceIds)
			? candidate.analysisSourceIds.map(String).filter(Boolean)
			: [],
		brandVoiceGuidance: Array.isArray(candidate.brandVoiceGuidance)
			? candidate.brandVoiceGuidance
					.map(String)
					.map((item) => item.trim())
					.filter(Boolean)
			: [],
		onboardingRevision: normalizeOnboardingRevision(candidate.onboardingRevision),
		firstResultAttempt: normalizeOnboarding1FirstResultAttempt(candidate.firstResultAttempt),
		summary: candidate.summary && typeof candidate.summary === 'object' ? candidate.summary : null,
		// Local storage is not question authority. The persisted chat message remains visible,
		// while the next answer is resolved against the scoped server-side discovery run.
		pendingDiscoveryQuestion: null
	});
	const variants = restored.summary?.calibrationVariants ?? [];
	const hasCalibrationAuthority =
		restored.stage === 'memory_review' &&
		Boolean(restored.brandVoiceRunId.trim()) &&
		Boolean(restored.summary?.calibrationChoiceSetRef?.trim()) &&
		variants.length === 2 &&
		variants.every((variant) => Boolean(variant.choiceRef?.trim()));
	if (restored.stage === 'memory_review' && !hasCalibrationAuthority) {
		return createOnboarding1FlowState({
			...restored,
			stage: 'awaiting_fact_confirmation',
			brandVoiceRunId: '',
			selectedCalibrationId: '',
			selectedCalibrationChoiceRef: '',
			summary: restored.summary
				? {
						...restored.summary,
						calibrationChoiceSetRef: undefined,
						calibrationVariants: []
					}
				: null
		});
	}
	return restored;
};

/**
 * A completed discovery response can outlive the local stage write when a
 * stream finishes at the same time as a reload or another composer turn. The
 * summary and its server-owned calibration authority are the durable proof
 * that the review is actionable; the transient analysis stage alone is not.
 */
export const hasOnboarding1BrandMemoryReview = (flow: Onboarding1FlowState): boolean => {
	const variants = flow.summary?.calibrationVariants ?? [];
	return (
		[
			'awaiting_brand_seed',
			'awaiting_brand_voice',
			'memory_review',
			'analyzing_memory',
			'applying_memory'
		].includes(flow.stage) &&
		flow.brandMemoryAnalysisStatus !== 'running' &&
		Boolean(
			flow.brandVoiceRunId &&
			flow.summary?.calibrationChoiceSetRef &&
			variants.length === 2 &&
			variants[0]?.choiceRef?.trim() !== variants[1]?.choiceRef?.trim() &&
			variants.every((variant) => variant.choiceRef?.trim())
		)
	);
};

/**
 * Free-form text beside the two Brand Voice cards is not automatically a new
 * source. Only a message that carries an explicit brand correction should
 * reopen analysis; ordinary deliverable requests stay on the review and wait
 * for the user's style choice. This keeps a second chat turn from silently
 * superseding the run that owns the visible cards.
 */
export const isOnboarding1MemoryCorrectionInput = (input: string): boolean => {
	const text = input.trim();
	if (!text || /^(?:почему|зачем|что это|что можно|как это)(?:\s|$)/iu.test(text)) {
		return false;
	}
	if (
		isOnboarding1PresentationCorrectionClause(text) ||
		isOnboarding1OfferingCorrectionClause(text)
	) {
		return true;
	}
	if (
		/(?:аудитор|клиент|покупател|подписчик|читател|тем[аы]|рубри|продукт|услуг|предложен|факт|описан|формулиров)/iu.test(
			text
		)
	) {
		return /(?:исправ|измен|помен|уточн|замен|добав|убер|неверн|неправил|это\s+не|должн|нужно|хочу)/iu.test(
			text
		);
	}
	return /(?:мягче|теплее|спокойнее|живее|суше|строже|проще|короче|подробнее|официальнее|\bcorrection\b)/iu.test(
		text
	);
};

const normalizeOnboarding1FirstResultAttempt = (value: unknown): number => {
	const attempt = Number(value);
	return Number.isSafeInteger(attempt) && attempt >= 1 ? attempt : 1;
};

const makeIdempotencyKey = (prefix: string, id = '') => {
	const suffix =
		typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}`;
	return `${prefix}-${id || 'onboarding1'}-${suffix}`;
};

export type Onboarding1FirstResultOperationIdentity = {
	stateId: string;
	firstResultType: FirstResultType;
	customRequest: string;
	platforms: string[];
	variantCount: number;
	selectedCalibrationId: Onboarding1CalibrationVariantId | '';
	selectedCalibrationChoiceRef: string;
	calibrationChoiceSetRef: string;
	brandVoiceRunId: string;
	attempt: number;
	expectedRevision?: number | null;
};

export type Onboarding1FirstResultIdempotencyKeys = {
	select: string;
	create: string;
	complete: string;
};

/**
 * First-result requests are safe to replay only when the logical task is the same.
 * Keep the identity deterministic across reloads while leaving unrelated onboarding
 * mutations on their existing per-request UUID keys.
 */
export const buildOnboarding1FirstResultIdempotencyKeys = (
	identity: Onboarding1FirstResultOperationIdentity
): Onboarding1FirstResultIdempotencyKeys => {
	const canonicalIdentity = JSON.stringify({
		version: 1,
		state_id: identity.stateId.trim(),
		first_result_type: identity.firstResultType,
		custom_request: identity.customRequest.trim(),
		platforms: identity.platforms.map((platform) => platform.trim().toLowerCase()),
		variant_count: Math.max(1, Math.floor(identity.variantCount)),
		brand_voice: {
			calibration_id: identity.selectedCalibrationId,
			choice_ref: identity.selectedCalibrationChoiceRef.trim(),
			choice_set_ref: identity.calibrationChoiceSetRef.trim(),
			run_id: identity.brandVoiceRunId.trim()
		},
		attempt: normalizeOnboarding1FirstResultAttempt(identity.attempt)
	});
	const digest = sha256(canonicalIdentity).slice(0, 40);
	const selectDigest = sha256(
		JSON.stringify({
			identity: canonicalIdentity,
			expected_revision: normalizeOnboardingRevision(identity.expectedRevision)
		})
	).slice(0, 40);
	const statePart = encodeURIComponent(identity.stateId.trim() || 'onboarding1');
	return {
		select: `onboarding1-result-select-${statePart}-${selectDigest}`,
		create: `onboarding1-result-create-${statePart}-${digest}`,
		complete: `onboarding1-complete-${statePart}-${digest}`
	};
};

export class Onboarding1FirstResultRevisionConflictError extends Error {
	readonly code = 'onboarding1_first_result_revision_conflict';
	readonly revision: number | null;

	constructor(revision: number | null) {
		super(ONBOARDING_FIRST_RESULT_REVISION_CONFLICT_MESSAGE);
		this.name = 'Onboarding1FirstResultRevisionConflictError';
		this.revision = normalizeOnboardingRevision(revision);
	}
}

export class Onboarding1ExplicitResultFailureError extends Error {
	readonly code = 'onboarding1_explicit_result_failure';
	readonly attempt: number;
	readonly revision: number | null;
	readonly clarification: OnboardingFirstResultClarification | null;

	constructor(
		message: string,
		attempt: number,
		revision?: number | null,
		result?: FirstDraftsResponse
	) {
		super(message);
		this.name = 'Onboarding1ExplicitResultFailureError';
		this.clarification = result ? projectOnboardingFirstResultClarification(result) : null;
		this.attempt = normalizeOnboarding1FirstResultAttempt(attempt);
		this.revision = normalizeOnboardingRevision(revision);
	}
}

export const isOnboarding1ExplicitResultFailure = (
	error: unknown
): error is Onboarding1ExplicitResultFailureError =>
	error instanceof Onboarding1ExplicitResultFailureError ||
	(typeof error === 'object' &&
		error !== null &&
		(error as { code?: unknown }).code === 'onboarding1_explicit_result_failure');

/** Advance only a server-confirmed failed result; timeouts keep the same replay key. */
export const resolveOnboarding1ResultRetryFlow = (
	flow: Onboarding1FlowState,
	error: unknown
): Onboarding1FlowState => {
	const candidate = error as { code?: unknown; revision?: unknown } | null;
	const revision = normalizeOnboardingRevision(candidate?.revision);
	const clarification = clarificationFromOnboardingResultError(error, flow.stateId);
	return createOnboarding1FlowState({
		...flow,
		...(clarification ? { firstResultClarification: clarification } : {}),
		onboardingRevision:
			(candidate?.code === 'onboarding1_explicit_result_failure' ||
				candidate?.code === 'onboarding1_first_result_revision_conflict') &&
			revision !== null
				? revision
				: flow.onboardingRevision,
		firstResultAttempt: isOnboarding1ExplicitResultFailure(error)
			? normalizeOnboarding1FirstResultAttempt(flow.firstResultAttempt) + 1
			: normalizeOnboarding1FirstResultAttempt(flow.firstResultAttempt)
	});
};

const unique = (values: string[]) =>
	Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));

export const extractOnboarding1Links = (value: string): string[] =>
	extractOnboardingSourceLinks(value);

export const removeOnboarding1Links = (value: string): string => removeOnboardingSourceLinks(value);

export const isOnboarding1BrandSeedSufficient = (value: string): boolean => {
	if (extractOnboarding1Links(value).length > 0) return true;

	const description = removeOnboarding1Links(value);
	const setupVocabulary = new Set([
		'brand',
		'memory',
		'бренд',
		'бренда',
		'бренду',
		'для',
		'другого',
		'мой',
		'моего',
		'настроить',
		'нового',
		'новый',
		'память',
		'помоги',
		'создать',
		'стиль'
	]);
	const informativeWords = Array.from(
		new Set(
			(description.match(/[\p{L}\p{N}]+/gu) ?? [])
				.map((word) => word.toLocaleLowerCase())
				.filter((word) => word.length >= 3 && !setupVocabulary.has(word))
		)
	);

	return informativeWords.join(' ').length >= 24 && informativeWords.length >= 5;
};

export class Onboarding1GenerationSourceError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'Onboarding1GenerationSourceError';
	}
}

const isUsefulInlineGenerationMaterial = (value: string, hasLinks: boolean) => {
	const clean = value.trim();
	if (!clean) return false;
	if (!hasLinks) return clean.length >= 40;
	return clean.length >= 280 || clean.split(/\n{2,}/).length >= 3;
};

export const prepareOnboarding1GenerationSources = async ({
	token,
	brandId,
	sourceText
}: {
	token: string;
	brandId: string;
	sourceText: string;
}): Promise<{ sourceIds: string[]; sourceCount: number }> => {
	const effectiveBrandId = String(brandId ?? '').trim();
	if (!effectiveBrandId) {
		throw new Onboarding1GenerationSourceError(
			'Не удалось определить бренд для подготовки постов. Обновите страницу и попробуйте ещё раз.'
		);
	}

	const links = extractOnboarding1Links(sourceText);
	const inlineMaterial = removeOnboarding1Links(sourceText);
	const sourceIds: string[] = [];
	let unavailableMessage = '';

	if (links.length) {
		const analysis = await analyzeOnboardingOnAPILinks(token, {
			links,
			client_request_id: makeIdempotencyKey('onboarding1-generation-analysis', effectiveBrandId)
		});
		for (const item of analysis.items ?? []) {
			const rawText = String(item.source_text ?? item.text ?? '').trim();
			if (
				!rawText ||
				['failed', 'fallback', 'unavailable', 'unsupported', 'authorization_required'].includes(
					item.status
				)
			) {
				unavailableMessage ||= String(item.user_message ?? '').trim();
				continue;
			}
			const created = await createContentFactorySource(token, {
				brand_id: effectiveBrandId,
				type: item.platform === 'website' ? 'website' : 'social_link',
				title: item.title || item.normalized_url || item.url,
				raw_text: rawText,
				source_url: item.normalized_url || item.url,
				metadata_json: {
					purpose: 'generation_material',
					module: 'on-api',
					platform: item.platform,
					social_type: item.social_type,
					source_is_untrusted_data: true,
					can_schedule: false,
					can_publish: false
				},
				idempotency: {
					idempotency_key: makeIdempotencyKey('onboarding1-generation-link', effectiveBrandId)
				}
			});
			if (created.id) sourceIds.push(created.id);
		}
	}

	if (isUsefulInlineGenerationMaterial(inlineMaterial, links.length > 0)) {
		const created = await createContentFactorySource(token, {
			brand_id: effectiveBrandId,
			type: 'text',
			title: 'Материал пользователя для постов',
			raw_text: inlineMaterial,
			metadata_json: {
				purpose: 'generation_material',
				source_is_untrusted_data: true,
				can_schedule: false,
				can_publish: false
			},
			idempotency: {
				idempotency_key: makeIdempotencyKey('onboarding1-generation-text', effectiveBrandId)
			}
		});
		if (created.id) sourceIds.push(created.id);
	}

	if (!sourceIds.length) {
		throw new Onboarding1GenerationSourceError(
			unavailableMessage ||
				'Не получилось прочитать материал. Пришлите другую публичную ссылку либо вставьте текст прямо в сообщение.'
		);
	}

	return { sourceIds: unique(sourceIds), sourceCount: sourceIds.length };
};

export const isOnboarding1Greeting = (value: string): boolean =>
	/^(?:привет|здравствуй(?:те)?|добрый\s+(?:день|вечер|утро)|как\s+дела|как\s+ты|как\s+поживаешь|как\s+у\s+тебя\s+дела|что\s+нового|что\s+ты\s+умеешь|что\s+можешь|помоги|hi|hello)[!.?\s]*$/i.test(
		value.trim()
	);

export const isOnboarding1CapabilityQuestion = (value: string): boolean =>
	/(?:^|[,.!?;:\s])(?:что\s+ты\s+умеешь|что\s+можешь|что\s+здесь\s+можно\s+сделать)(?:$|[,.!?;:\s])/i.test(
		value.trim()
	);

/**
 * High-precision fallback for messages that must stay in ordinary conversation
 * even when the composer has a selected onboarding direction. The selected
 * direction is context, not a command: it must not capture greetings, product
 * questions or an explicit request to talk first.
 *
 * Keep this intentionally conservative. A message containing a concrete
 * content action continues to the semantic router, so "привет, напиши пост"
 * still starts generation instead of being mistaken for small talk.
 */
export const isOnboarding1ConversationOnlyPrompt = (value: string): boolean => {
	const text = value.trim();
	if (!text) return false;

	const hasConcreteContentAction =
		/(?:напиш|созда|сдела|собер|подготов|сгенер|придум|отредакт|перепиш|адаптир|опублику|запланир|проанализир)\S*(?:\s|$).*(?:пост|текст|план|тем|сер|кампан|контент|карточ|публикац)|(?:пост|текст|план|тем|сер|кампан|контент|карточ|публикац)\S*(?:\s|$).*(?:напиш|созда|сдела|собер|подготов|сгенер|придум|отредакт|перепиш|адаптир|опублику|запланир|проанализир)/i.test(
			text
		);
	if (hasConcreteContentAction) return false;

	if (isOnboarding1Greeting(text) || isOnboarding1CapabilityQuestion(text)) return true;

	return (
		/^(?:не\s+знаю|не\s+уверен(?:а)?|пока\s+не\s+знаю)[!.?\s]*$/i.test(text) ||
		/(?:можно|хочу|давай)(?:\s+сначала)?(?:\s+просто)?\s+(?:спросить|задать\s+вопрос|поболтать|поговорить|пообщаться)/i.test(
			text
		)
	);
};

export const shouldUseOnboarding1ConversationFallback = ({
	flowStage,
	fileCount,
	prompt
}: {
	flowStage: Onboarding1FlowStage;
	fileCount: number;
	prompt: string;
}): boolean => {
	if (fileCount !== 0 || !isOnboarding1ConversationOnlyPrompt(prompt)) {
		return false;
	}
	const isUncertainAnswer = /^(?:не\s+знаю|не\s+уверен(?:а)?|пока\s+не\s+знаю)[!.?\s]*$/i.test(
		prompt.trim()
	);
	return (
		!isUncertainAnswer ||
		flowStage === 'idle' ||
		flowStage === 'awaiting_source_choice' ||
		flowStage === 'awaiting_task'
	);
};

export const resolveOnboarding1DirectionIndex = (value: string): number | null => {
	const text = value.trim().toLowerCase();
	if (/(?:план|календар|тем(?:а|ы)|кампани|расписан)/i.test(text)) return 0;
	if (/(?:пост|текст|публикаци|написа|переписа|редактур)/i.test(text)) return 1;
	if (/(?:reels?|shorts?|рилс|шортс|ролик|видео|сценари)/i.test(text)) return 2;
	if (/(?:с нуля|стратеги|система|старт|не знаю|помоги выбрать|выбери сам)/i.test(text)) return 3;
	return null;
};

const onboarding1PostIntentIds = new Set([
	'write_posts_in_brand_voice',
	'create_post_series',
	'create_posts_from_sources',
	'rewrite_text'
]);

const onboarding1BrandPreparationStages = new Set<Onboarding1FlowStage>([
	'awaiting_source_choice',
	'awaiting_source',
	'awaiting_brand_seed',
	'analyzing_memory',
	'awaiting_fact_confirmation',
	'applying_memory',
	'memory_review',
	'awaiting_memory_correction'
]);

export const isOnboarding1PostIntent = (intentId: string): boolean =>
	onboarding1PostIntentIds.has(intentId);

export const resolveOnboarding1FirstResultFlow = (
	flow: Onboarding1FlowState
): Onboarding1FlowState => {
	const hasConfirmedCalibration =
		flow.brandReady && Boolean(flow.selectedCalibrationId && flow.selectedCalibrationChoiceRef);
	const shouldPrepareFirstPost =
		hasConfirmedCalibration &&
		(flow.intentId === 'help_choose_first_step' || flow.intentId === 'create_content_starter_kit');
	if (!shouldPrepareFirstPost) {
		return flow;
	}

	return createOnboarding1FlowState({
		...flow,
		intentId: 'write_posts_in_brand_voice',
		cardId: 'write-posts',
		modeLabel: 'Написать пост',
		composerPlaceholder: 'Опишите тему и главную мысль поста',
		// Keep an explicitly supplied user topic. A workflow-only request such as
		// “Подготовь два поста для Telegram” is a request for the next step, not
		// a topic. It must reach the visible topic-choice decision instead of
		// becoming a hidden brief for the first draft.
		topic:
			flow.topic.trim() !== flow.sourceContent.trim() && hasOnboarding1ExplicitPostTopic(flow.topic)
				? flow.topic.trim()
				: ''
	});
};

/**
 * A write-posts flow with a saved Brand Voice still needs one explicit topic
 * decision before any draft is generated. Workflow-only requests (for
 * example, a request to prepare two posts for a channel) are treated like an
 * empty topic; content-bearing requests continue through the generation path.
 */
const ONBOARDING1_GENERIC_POST_REQUEST_RE =
	/(?:подготов[\p{L}\p{N}_-]*|сдел[\p{L}\p{N}_-]*|напис[\p{L}\p{N}_-]*|созда[\p{L}\p{N}_-]*|сформир[\p{L}\p{N}_-]*|сгенерир[\p{L}\p{N}_-]*|нуж[\p{L}\p{N}_-]*|хоч[\p{L}\p{N}_-]*)|(?:пост[\p{L}\p{N}_-]*|публикац[\p{L}\p{N}_-]*|текст[\p{L}\p{N}_-]*|материал[\p{L}\p{N}_-]*|черновик[\p{L}\p{N}_-]*|карточк[\p{L}\p{N}_-]*)|(?:телеграм[\p{L}\p{N}_-]*|telegram[\p{L}\p{N}_-]*|тг|вк|vk|инстаграм[\p{L}\p{N}_-]*|instagram[\p{L}\p{N}_-]*|канал[\p{L}\p{N}_-]*|блог[\p{L}\p{N}_-]*|соцсет[\p{L}\p{N}_-]*|площадк[\p{L}\p{N}_-]*)|(?:один|одна|одно|два|две|три|четыре|пять|шесть|семь|восемь|девять|десять|несколько|перв[\p{L}\p{N}_-]*|готов[\p{L}\p{N}_-]*|отдель[\p{L}\p{N}_-]*|разн[\p{L}\p{N}_-]*|сер[\p{L}\p{N}_-]*|нужн[\p{L}\p{N}_-]*|сво[\p{L}\p{N}_-]*|мо[\p{L}\p{N}_-]*|ваш[\p{L}\p{N}_-]*|теб[\p{L}\p{N}_-]*|себ[\p{L}\p{N}_-]*|предпринимател[\p{L}\p{N}_-]*|клиент[\p{L}\p{N}_-]*|покупател[\p{L}\p{N}_-]*|подписчик[\p{L}\p{N}_-]*|аудитор[\p{L}\p{N}_-]*|люд[\p{L}\p{N}_-]*|женщин[\p{L}\p{N}_-]*|мужчин[\p{L}\p{N}_-]*|бизнес[\p{L}\p{N}_-]*|команд[\p{L}\p{N}_-]*|специалист[\p{L}\p{N}_-]*|родител[\p{L}\p{N}_-]*|мам[\p{L}\p{N}_-]*|пап[\p{L}\p{N}_-]*|взросл[\p{L}\p{N}_-]*|эксперт[\p{L}\p{N}_-]*)|(?:для|в|на|по|из|о|об|про|к|с|со|и|без|после|до|тему|теме|стил[\p{L}\p{N}_-]*|бренд[\p{L}\p{N}_-]*|voice|подач[\p{L}\p{N}_-]*|формат[\p{L}\p{N}_-]*)|\d+/giu;

export const hasOnboarding1ExplicitPostTopic = (value: string): boolean => {
	const normalized = value
		.trim()
		.toLocaleLowerCase('ru-RU')
		.replace(/[«»"“”.,!?;:()[\]{}:—–-]/gu, ' ')
		.replace(/\s+/gu, ' ')
		.trim();
	if (!normalized) return false;
	const residual = normalized
		.replace(ONBOARDING1_GENERIC_POST_REQUEST_RE, ' ')
		.replace(/\s+/gu, ' ')
		.trim();
	return residual.length > 0;
};

export const shouldOfferOnboarding1PostTopicChoice = (flow: Onboarding1FlowState): boolean =>
	flow.brandReady &&
	['write_posts_in_brand_voice', 'create_post_series'].includes(flow.intentId) &&
	!hasOnboarding1ExplicitPostTopic(flow.topic);

export const retargetOnboarding1Flow = ({
	flow,
	actionId,
	card,
	brandContext,
	stageOverride
}: {
	flow: Onboarding1FlowState;
	actionId: string;
	card: Onboarding1InfoCard;
	brandContext: { brandReady: boolean; workspaceId: string; brandId: string };
	stageOverride?: Onboarding1FlowState['stage'];
}): Onboarding1FlowState => {
	const sameIntent = flow.intentId === card.intent;
	const brandReady = brandContext.brandReady || flow.brandReady;
	const preparingBrand = !brandReady && onboarding1BrandPreparationStages.has(flow.stage);

	return createOnboarding1FlowState({
		...flow,
		brandReady,
		workspaceId: brandContext.workspaceId || flow.workspaceId,
		brandId: brandContext.brandId || flow.brandId,
		intentId: card.intent,
		actionId,
		cardId: card.id,
		modeLabel: card.badgeLabel,
		composerPlaceholder: card.composerPlaceholder,
		topic: sameIntent ? flow.topic : '',
		sourceContent: sameIntent || preparingBrand ? flow.sourceContent : '',
		requestedCount: sameIntent ? flow.requestedCount : 0,
		stage:
			stageOverride ??
			(brandReady ? 'awaiting_task' : preparingBrand ? flow.stage : 'awaiting_source_choice')
	});
};

export const resolveOnboarding1TopicContinuationFlow = ({
	flow,
	topic,
	requestedCount = flow.requestedCount
}: {
	flow: Onboarding1FlowState;
	topic: string;
	requestedCount?: number;
}): Onboarding1FlowState =>
	createOnboarding1FlowState({
		...flow,
		topic: topic.trim(),
		requestedCount,
		stage: flow.brandReady ? 'generating_result' : 'awaiting_source_choice'
	});

export const resolveOnboarding1InterruptedGenerationStage = (
	flow: Onboarding1FlowState
): Onboarding1FlowStage => {
	if (
		!isOnboarding1PostIntent(flow.intentId) ||
		!flow.brandReady ||
		flow.sourceMode === 'universal'
	) {
		return 'awaiting_task';
	}
	if (flow.intentId === 'create_post_series' && !flow.requestedCount) {
		return 'awaiting_series_count';
	}
	if (
		flow.intentId === 'write_posts_in_brand_voice' &&
		!hasOnboarding1ExplicitPostTopic(flow.topic)
	) {
		return 'awaiting_topic';
	}
	return 'memory_review';
};

export const shouldGenerateOnboarding1UniversalDraft = (_request: {
	brandReady: boolean;
	generationMode: string | null;
	intentId: string;
	taskBrief: string;
}): boolean => false;

export const resolveOnboarding1OpenedActionIndex = ({
	flow,
	hasModeBadge,
	input
}: {
	flow: Onboarding1FlowState;
	hasModeBadge: boolean;
	input: string;
}): number | null =>
	flow.stage === 'idle' && !hasModeBadge ? resolveOnboarding1DirectionIndex(input) : null;

export const looksLikeOnboarding1BrandSource = (value: string): boolean => {
	const text = value.trim();
	return (
		extractOnboarding1Links(text).length > 0 || text.length >= 140 || text.split(/\s+/).length >= 22
	);
};

export const isOnboarding1StartFromScratch = (value: string): boolean =>
	/(?:начинаю|начать|делаю|создаю|строю)\s+с\s+нуля|нет\s+(?:ещ[ёе]\s+)?(?:бренда|канала|сайта|материалов|текстов)|ничего\s+(?:ещ[ёе]\s+)?нет/i.test(
		value.trim()
	);

/**
 * Detects an explicit request to configure Brand Memory for a separate new brand.
 * This is intentionally narrower than generic brand/style mentions: using existing
 * memory or asking a question about it must remain ordinary conversation.
 */
export const isOnboarding1NewBrandSetupRequest = (value: string): boolean => {
	const normalized = value.replace(/\s+/g, ' ').trim();
	if (!normalized) return false;
	const setupAction =
		/(?:созда(?:ть|й|йте|ю|ём|ем)|настро(?:ить|й|йте|ю|им)|соб(?:рать|ери|ерите|еру|ерём|ерем)|сформир(?:овать|уй|уйте)|запуст(?:ить|и|ите)|перенастро(?:ить|й|йте))/i;
	const brandMemoryObject =
		/(?:brand\s*memory|памят(?:ь|и)\s+бренд|стил(?:ь|я)\s+(?:(?:нов[а-яё]*|друг[а-яё]*)\s+)?(?:бренд|проект|сервис|продукт|бизнес|компани[а-яё]*)|голос(?:а)?\s+бренд)/i;
	const separateBrandContext =
		/(?:нов(?:ый|ая|ое|ого|ой|ому|ую)\s+(?:бренд|проект|сервис|продукт|бизнес|компани[а-яё]*)|друг(?:ой|ая|ое|ого|ому|ую)\s+(?:бренд|проект|сервис|продукт|бизнес|компани[а-яё]*)|с\s+нуля)/i;
	const explicitSeedSyntax = /brand\s*memory\s*(?:для\s+)?(?:—|-|:)\s*\S+/i;
	return (
		setupAction.test(normalized) &&
		brandMemoryObject.test(normalized) &&
		(separateBrandContext.test(normalized) || explicitSeedSyntax.test(normalized))
	);
};

/**
 * Brand Memory collection is a transactional sub-flow. While it is waiting for
 * a seed, review decision, or correction, a previously selected content mode
 * must not retarget the next message before the sub-flow can consume it.
 */
export const shouldPreserveOnboarding1BrandMemoryStage = (stage: Onboarding1FlowStage): boolean =>
	stage === 'awaiting_brand_seed' ||
	stage === 'awaiting_fact_confirmation' ||
	stage === 'awaiting_brand_voice' ||
	stage === 'awaiting_discovery_question' ||
	stage === 'memory_review' ||
	stage === 'awaiting_memory_correction';

const ONBOARDING1_MAX_REQUESTED_POST_COUNT = 10;
const ONBOARDING1_COUNT_WORDS: Readonly<Record<string, number>> = Object.freeze({
	один: 1,
	одна: 1,
	одно: 1,
	одну: 1,
	одного: 1,
	два: 2,
	две: 2,
	двух: 2,
	три: 3,
	трех: 3,
	трёх: 3,
	четыре: 4,
	четырех: 4,
	четырёх: 4,
	пять: 5,
	пяти: 5,
	шесть: 6,
	шести: 6,
	семь: 7,
	семи: 7,
	восемь: 8,
	восьми: 8,
	девять: 9,
	девяти: 9,
	десять: 10,
	десяти: 10
});
const ONBOARDING1_COUNT_WORD_PATTERN = Object.keys(ONBOARDING1_COUNT_WORDS).join('|');
const ONBOARDING1_COUNT_OBJECT_PATTERN =
	'(?:пост(?:а|ов|ы)?|публикаци(?:я|и|й|ю)|тем(?:а|ы)?|вариант(?:а|ов|ы)?)';
const ONBOARDING1_COUNT_QUALIFIER_PATTERN = '(?:(?:готов\\w*|понятн\\w*|разн\\w*)\\s+)?';
const ONBOARDING1_COUNT_PREFIX_PATTERN = '(?:^|[\\s([{«])(?:из\\s+)?';

const normalizeOnboarding1RequestedCount = (value: unknown): number => {
	const count = Number(value);
	return Number.isSafeInteger(count) && count >= 1 && count <= ONBOARDING1_MAX_REQUESTED_POST_COUNT
		? count
		: 0;
};

const hasUnsupportedOnboarding1RequestedCount = (value: string): boolean => {
	const withoutLinks = value.replace(/https?:\/\/[^\s<>()]+/gi, ' ');
	return new RegExp(
		`${ONBOARDING1_COUNT_PREFIX_PATTERN}-?\\d+(?:[.,]\\d+)?\\s+${ONBOARDING1_COUNT_QUALIFIER_PATTERN}${ONBOARDING1_COUNT_OBJECT_PATTERN}(?=\\s|[.,!?;:]|$)`,
		'i'
	).test(withoutLinks);
};

const resolveOnboarding1RequestedCount = ({
	requestedCount,
	prompt,
	fallback
}: {
	requestedCount: number;
	prompt: string;
	fallback: number;
}): number => {
	const supplied = normalizeOnboarding1RequestedCount(requestedCount);
	if (supplied) return supplied;
	const extracted = extractOnboarding1RequestedCount(prompt);
	if (extracted) return extracted;
	if (requestedCount || hasUnsupportedOnboarding1RequestedCount(prompt)) {
		throw new RangeError('Количество постов должно быть целым числом от 1 до 10');
	}
	return fallback;
};

const onboarding1PostCountLabel = (count: number): string => {
	const remainder100 = count % 100;
	const remainder10 = count % 10;
	const noun =
		remainder100 >= 11 && remainder100 <= 14
			? 'постов'
			: remainder10 === 1
				? 'пост'
				: remainder10 >= 2 && remainder10 <= 4
					? 'поста'
					: 'постов';
	return `${count} ${noun}`;
};

export const extractOnboarding1RequestedCount = (value: string): number => {
	const withoutLinks = value.replace(/https?:\/\/[^\s<>()]+/gi, ' ').trim();
	if (
		/(?:^|\s)(?:1|один|одну|перв(?:ый|ую))\s+(?:готов(?:ый|ую)\s+|понятн(?:ый|ую)\s+)?(?:пост|публикаци(?:ю|я))(?=\s|[.,!?;:]|$)/i.test(
			withoutLinks
		)
	) {
		return 1;
	}
	const standalone = withoutLinks.match(/^\s*(10|[1-9])\s*[.!?]?\s*$/)?.[1];
	if (standalone) return Number(standalone);
	const standaloneWord = withoutLinks.match(
		new RegExp(`^\\s*(${ONBOARDING1_COUNT_WORD_PATTERN})\\s*[.!?]?\\s*$`, 'i')
	)?.[1];
	if (standaloneWord) return ONBOARDING1_COUNT_WORDS[standaloneWord.toLowerCase()] ?? 0;
	const written = withoutLinks.match(
		new RegExp(
			`${ONBOARDING1_COUNT_PREFIX_PATTERN}(${ONBOARDING1_COUNT_WORD_PATTERN})\\s+${ONBOARDING1_COUNT_QUALIFIER_PATTERN}${ONBOARDING1_COUNT_OBJECT_PATTERN}(?=\\s|[.,!?;:]|$)`,
			'i'
		)
	)?.[1];
	if (written) {
		return ONBOARDING1_COUNT_WORDS[written.toLowerCase()] ?? 0;
	}
	const explicit = withoutLinks.match(
		new RegExp(
			`${ONBOARDING1_COUNT_PREFIX_PATTERN}(10|[1-9])\\s+${ONBOARDING1_COUNT_QUALIFIER_PATTERN}${ONBOARDING1_COUNT_OBJECT_PATTERN}(?=\\s|[.,!?;:]|$)`,
			'i'
		)
	)?.[1];
	return explicit ? Number(explicit) : 0;
};

export const resolveOnboarding1PromptRetarget = ({
	flow,
	actionId,
	card,
	brandContext,
	prompt
}: {
	flow: Onboarding1FlowState;
	actionId: string;
	card: Onboarding1InfoCard;
	brandContext: { brandReady: boolean; workspaceId: string; brandId: string };
	prompt: string;
}): Onboarding1FlowState => {
	const nextFlow = retargetOnboarding1Flow({
		flow,
		actionId,
		card,
		brandContext
	});
	if (card.id !== 'create-post-series') {
		if (card.onboardingFlow === 'guided-result' || card.id === 'write-posts') {
			return createOnboarding1FlowState({
				...nextFlow,
				topic: prompt.trim()
			});
		}
		if (card.id === 'posts-from-materials' || card.id === 'rewrite-text') {
			return createOnboarding1FlowState({
				...nextFlow,
				sourceContent: prompt.trim(),
				requestedCount:
					card.id === 'posts-from-materials'
						? extractOnboarding1RequestedCount(prompt) || nextFlow.requestedCount
						: nextFlow.requestedCount
			});
		}
		return nextFlow;
	}

	const requestedCount = extractOnboarding1RequestedCount(prompt);
	return createOnboarding1FlowState({
		...nextFlow,
		topic: prompt.trim(),
		requestedCount,
		stage: requestedCount ? nextFlow.stage : 'awaiting_series_count'
	});
};

/**
 * Keep the user's deliverable attached while a long brand description is being
 * analysed. A selected start card can already have put the flow into
 * `awaiting_source_choice`; once the same message is recognised as source
 * material it advances to `awaiting_source`. Neither transition means that the
 * task contained in that message may be discarded.
 */
export const resolveOnboarding1AnalysisFlow = ({
	flow,
	card,
	prompt
}: {
	flow: Onboarding1FlowState;
	card: Onboarding1InfoCard;
	prompt: string;
}): Onboarding1FlowState => {
	const isInitialBrandPreparation =
		!flow.brandReady &&
		flow.stage !== 'awaiting_memory_correction' &&
		onboarding1BrandPreparationStages.has(flow.stage);
	const taskText = prompt.trim();

	return createOnboarding1FlowState({
		...flow,
		topic:
			flow.topic.trim() ||
			(isInitialBrandPreparation &&
			(card.onboardingFlow === 'guided-result' ||
				card.id === 'write-posts' ||
				card.id === 'create-post-series')
				? taskText
				: ''),
		sourceContent:
			flow.sourceContent.trim() ||
			(isInitialBrandPreparation &&
			(card.id === 'posts-from-materials' || card.id === 'rewrite-text')
				? taskText
				: ''),
		requestedCount:
			card.id === 'create-post-series' || card.id === 'posts-from-materials'
				? extractOnboarding1RequestedCount(taskText) || flow.requestedCount
				: flow.requestedCount
	});
};

export const resolveOnboarding1TopicSuggestionCount = ({
	prompt = ''
}: {
	prompt?: string;
}): number => {
	const explicitCount = extractOnboarding1RequestedCount(prompt);
	return Math.min(5, Math.max(2, explicitCount || 5));
};

export const resolveOnboarding1TargetPlatforms = ({
	prompt = '',
	connectedPlatforms = []
}: {
	prompt?: string;
	connectedPlatforms?: string[];
}): string[] => {
	return resolveContentFactoryTargetPlatforms({
		prompt,
		connectedPlatforms
	}).targetPlatforms;
};

export const resolveOnboarding1PublishingTargetState = ({
	stateKnown,
	targetPlatforms = [],
	connectedPlatforms = []
}: {
	stateKnown: boolean;
	targetPlatforms?: string[];
	connectedPlatforms?: string[];
}): 'unknown' | 'connected' | 'missing' => {
	if (!stateKnown) return 'unknown';
	const normalizedTargets = new Set(
		targetPlatforms.map((platform) => platform.trim().toLowerCase()).filter(Boolean)
	);
	const normalizedConnected = new Set(
		connectedPlatforms.map((platform) => platform.trim().toLowerCase()).filter(Boolean)
	);
	if (normalizedTargets.size === 0) {
		return normalizedConnected.size > 0 ? 'connected' : 'missing';
	}
	return Array.from(normalizedTargets).every((platform) => normalizedConnected.has(platform))
		? 'connected'
		: 'missing';
};

type Onboarding1RuntimeBrand = {
	id?: string;
	brand_id?: string;
	workspace_id?: string;
	runtime_context_ready?: boolean;
	has_runtime_artifacts?: boolean;
	memory_count?: number;
	voice_profile_count?: number;
	platform_style_count?: number;
	style_fingerprint_present?: boolean;
	emoji_fingerprint_present?: boolean;
	formatting_fingerprint_present?: boolean;
	composition_fingerprint_present?: boolean;
	feedback_rules_used?: boolean;
};

export const isOnboarding1BrandReady = (
	brand: Onboarding1RuntimeBrand | null | undefined
): boolean =>
	Boolean(
		brand &&
		(Number(brand.memory_count ?? 0) > 0 ||
			Number(brand.voice_profile_count ?? 0) > 0 ||
			Number(brand.platform_style_count ?? 0) > 0 ||
			brand.style_fingerprint_present === true ||
			brand.emoji_fingerprint_present === true ||
			brand.formatting_fingerprint_present === true ||
			brand.composition_fingerprint_present === true ||
			brand.feedback_rules_used === true)
	);

export const resolveOnboarding1BrandContext = (
	brand: Onboarding1RuntimeBrand | null | undefined
): { brandReady: boolean; workspaceId: string; brandId: string } => {
	const brandReady = isOnboarding1BrandReady(brand);
	return {
		brandReady,
		workspaceId: brandReady ? String(brand?.workspace_id ?? '').trim() : '',
		brandId: brandReady ? String(brand?.id ?? brand?.brand_id ?? '').trim() : ''
	};
};

// Channel discovery and Brand Voice readiness are independent. A newly created
// brand may already own connected channels before its memory is ready. Keep the
// generation context guarded above, but retain the brand identity for read-only
// publishing-context discovery.
export const resolveOnboarding1PublishingBrandId = (
	brand: Onboarding1RuntimeBrand | null | undefined
): string => String(brand?.id ?? brand?.brand_id ?? '').trim();

export const getOnboarding1ReadyBrandPrompt = (cardId: string): string => {
	if (cardId === 'posts-from-materials') {
		return 'Пришлите ссылку или вставьте материал — я превращу его в три готовых поста в вашем стиле.';
	}
	if (cardId === 'rewrite-text') {
		return 'Вставьте текст и одним предложением напишите, что нужно улучшить.';
	}
	if (cardId === 'create-post-series') {
		return 'Напишите тему серии. По умолчанию подготовлю 3 поста; при желании укажите число от 1 до 10.';
	}
	return 'Напишите тему и главную мысль — я сразу подготовлю готовый пост в вашем стиле.';
};

export const buildOnboarding1ReadyBrandTask = ({
	cardId,
	prompt,
	requestedCount = 0,
	targetPlatforms = [],
	forceTargetPlatforms = false,
	useBrandContext = true
}: {
	cardId: string;
	prompt: string;
	requestedCount?: number;
	targetPlatforms?: string[];
	forceTargetPlatforms?: boolean;
	useBrandContext?: boolean;
}): string => {
	const cleanPrompt = prompt.trim();
	const normalizedPlatforms = forceTargetPlatforms
		? Array.from(
				new Set(
					targetPlatforms.map((platform) => String(platform).trim().toLowerCase()).filter(Boolean)
				)
			)
		: resolveOnboarding1TargetPlatforms({
				prompt: cleanPrompt,
				connectedPlatforms: targetPlatforms
			});
	const targetInstruction = normalizedPlatforms.length
		? `Адаптируй результат под ${normalizedPlatforms.join(', ')}.`
		: 'Подготовь универсальный черновик без неподтверждённой привязки к площадке.';
	if (cardId === 'posts-from-materials') {
		const count = resolveOnboarding1RequestedCount({
			requestedCount,
			prompt: cleanPrompt,
			fallback: 3
		});
		return buildContentFactoryInternalTask([
			`Подготовь ${onboarding1PostCountLabel(count)} в стиле бренда.`,
			targetInstruction,
			'Каждый пост должен быть полноценным и готовым к публикации.',
			'Опирайся только на факты из материала пользователя, не добавляй неподтверждённые детали.',
			`Материал:\n${cleanPrompt}`
		]);
	}
	if (cardId === 'rewrite-text') {
		return buildContentFactoryInternalTask([
			'Подготовь 1 пост-карточку с результатом редакции предоставленного текста в стиле бренда.',
			'Пост-карточка — только технический контейнер результата: не создавай новый полноценный пост, новый текст или новый сюжет.',
			'Верни одну отредактированную версию без пояснений до или после неё.',
			'Сохрани тему, смысл и подтверждённые факты исходника, не заменяй их новым материалом.',
			'Пожелания пользователя имеют приоритет. Если он просит убрать общие слова, не добавляй типовые вступления и эмоциональные заполнители.',
			'Не добавляй новые характеристики, обстоятельства, обещания или товарные факты.',
			'Сохрани масштаб исходника: одно предложение остаётся одним-двумя предложениями, короткий абзац — коротким абзацем.',
			'Если пользователь просит сделать короче, итог не должен быть длиннее редактируемого текста.',
			'Не добавляй вступление, вывод, CTA, эмодзи или оформление Telegram-поста, если их не было в исходнике и пользователь отдельно об этом не просил.',
			`Текст и пожелания пользователя:\n${cleanPrompt}`
		]);
	}
	if (cardId === 'create-post-series') {
		const count = resolveOnboarding1RequestedCount({
			requestedCount,
			prompt: cleanPrompt,
			fallback: 3
		});
		return buildContentFactoryInternalTask([
			`Напиши ${onboarding1PostCountLabel(count)} в стиле бренда.`,
			targetInstruction,
			'Каждый пост должен быть полноценным: отдельный угол, законченная мысль и готовый к публикации текст.',
			`Тема и задача пользователя: ${cleanPrompt}`
		]);
	}
	if (cardId === 'suggest-topics') {
		const count = Math.min(
			5,
			Math.max(3, requestedCount || extractOnboarding1RequestedCount(cleanPrompt) || 5)
		);
		return buildContentFactoryInternalTask([
			`Подготовь подборку из ${count} разных тем для будущих публикаций${useBrandContext ? ' в стиле бренда' : ''}.`,
			'Это подбор направлений: не создавай черновики постов.',
			`Для каждой темы дай короткий заголовок, отдельный ракурс и понятное объяснение, почему она подходит${useBrandContext ? ' бренду' : ' задаче пользователя'}.`,
			`Запрос пользователя: ${cleanPrompt}`
		]);
	}
	return buildContentFactoryInternalTask([
		'Напиши один полноценный пост в стиле бренда.',
		targetInstruction,
		`Тема и главная мысль пользователя: ${cleanPrompt}`
	]);
};

export const onboarding1SeriesCountDecision = (): Onboarding1ComposerDecision => ({
	id: 'post-series-count',
	message: 'Сколько постов подготовить в первой серии?',
	confirmLabel: '3 поста',
	declineLabel: '5 постов'
});

/**
 * A saved Brand Voice is ready for a first post, but the next topic still
 * belongs to the person. Keep this as a composer-only decision so the choice
 * cannot be mistaken for a model prompt or silently generate a draft.
 */
export const onboarding1PostTopicDecision = (): Onboarding1ComposerDecision => ({
	id: 'post-topic-choice',
	message: 'Как выбрать тему первого поста?',
	confirmLabel: 'Подобрать тему',
	declineLabel: 'Прислать свою тему'
});

const requiredBrandSourceDecisionCopy = (intentId: string) => {
	if (['suggest_topics', 'suggest_short_video_ideas'].includes(intentId)) {
		return {
			message: 'Чтобы предложить подходящие темы, сначала познакомимся с вашим бизнесом.'
		};
	}
	if (
		[
			'create_content_plan',
			'fill_content_calendar',
			'plan_content_campaign',
			'suggest_content_strategy',
			'build_content_system'
		].includes(intentId)
	) {
		return {
			message: 'Чтобы собрать полезный план, сначала познакомимся с вашим бизнесом.'
		};
	}
	if (['create_post_series', 'create_posts_from_sources'].includes(intentId)) {
		return {
			message: 'Чтобы написать посты именно для вас, сначала познакомимся с вашим бизнесом.'
		};
	}
	if (intentId === 'rewrite_text') {
		return {
			message: 'Чтобы улучшить текст в вашем стиле, сначала познакомимся с вашим бизнесом.'
		};
	}
	return {
		message: 'Чтобы подготовить результат именно для вас, сначала познакомимся с вашим бизнесом.'
	};
};

export const onboarding1SourceDecision = (intentId = ''): Onboarding1ComposerDecision => {
	const copy = requiredBrandSourceDecisionCopy(intentId);
	return {
		id: 'brand-source-choice',
		message: `${copy.message} Пришлите ссылку на соцсеть или сайт, примеры постов либо краткое описание бизнеса.`,
		confirmLabel: 'Описать бизнес',
		declineLabel: 'Прислать ссылку',
		alternateLabel: 'Изменить задачу'
	};
};

const ONBOARDING1_COMPOSER_DECISION_STAGES = new Set<Onboarding1FlowStage>([
	'awaiting_source_choice',
	'awaiting_source',
	'awaiting_brand_seed',
	'awaiting_series_count',
	'awaiting_fact_confirmation',
	'awaiting_topic_choice'
]);

export const shouldUseOnboarding1ComposerDecisionResponse = (stage: Onboarding1FlowStage) =>
	ONBOARDING1_COMPOSER_DECISION_STAGES.has(stage);

export const onboarding1RecoveryDecision = (): Onboarding1ComposerDecision => ({
	id: 'onboarding-step-recovery',
	message: 'Генерация не завершилась. Продолжить?',
	confirmLabel: 'Повторить',
	declineLabel: 'Отмена'
});

export const onboarding1ResumeDecision = ({
	retryOnly = false
}: { retryOnly?: boolean } = {}): Onboarding1ComposerDecision => ({
	id: 'resume-onboarding-task',
	message: 'Генерация не завершилась. Продолжить?',
	confirmLabel: 'Повторить',
	...(retryOnly ? {} : { declineLabel: 'Отмена' })
});

const ONBOARDING1_RESUMABLE_STAGES = new Set<Onboarding1FlowStage>([
	'awaiting_direction',
	'awaiting_source_choice',
	'awaiting_source',
	'awaiting_brand_seed',
	'awaiting_fact_confirmation',
	'memory_review',
	'awaiting_memory_correction',
	'awaiting_series_count',
	'awaiting_topic_choice',
	'awaiting_topic',
	'awaiting_task'
]);

export const shouldOfferOnboarding1Resume = ({
	flow,
	lastActivityAt,
	now = Date.now(),
	minimumAgeMs = 6 * 60 * 60 * 1000,
	dismissed = false
}: {
	flow: Onboarding1FlowState;
	lastActivityAt: number;
	now?: number;
	minimumAgeMs?: number;
	dismissed?: boolean;
}) => {
	if (dismissed || !ONBOARDING1_RESUMABLE_STAGES.has(flow.stage)) return false;
	const normalizedActivityAt =
		lastActivityAt > 0 && lastActivityAt < 1_000_000_000_000
			? lastActivityAt * 1000
			: lastActivityAt;
	return normalizedActivityAt > 0 && now - normalizedActivityAt >= minimumAgeMs;
};

export const onboarding1CampaignNextDecision = (): Onboarding1ComposerDecision => ({
	id: 'campaign-next-step',
	message: 'Что сделать с готовым планом?',
	confirmLabel: 'Создать посты',
	declineLabel: 'Изменить план'
});

export const onboarding1FirstPostNextDecision = (): Onboarding1ComposerDecision => ({
	id: 'first-post-next-step',
	message: 'Что сделать дальше?',
	confirmLabel: 'Создать ещё один пост',
	declineLabel: 'Оставить этот пост'
});

const importIdentity = (value: Record<string, any>, key: 'brand_id' | 'workspace_id') =>
	String(value?.[key] ?? value?.metadata_json?.[key] ?? '').trim();

const ensureOnboardingState = async (
	token: string,
	flow: Onboarding1FlowState,
	startFresh = false
) => {
	const persistedStateId =
		(startFresh ? '' : flow.stateId) ||
		(typeof localStorage !== 'undefined'
			? startFresh
				? ''
				: (localStorage.getItem(onboarding1StateStorageKey) ?? '')
			: '');
	if (persistedStateId) {
		const existing = await getOnboardingState(token, persistedStateId).catch(() => null);
		if (existing?.state_id && existing.status !== 'completed' && existing.status !== 'skipped') {
			return existing;
		}
	}
	return await startOnboarding(token, { entry_kind: 'legacy_onboarding1' });
};

const readableList = (value: unknown) => {
	if (Array.isArray(value)) return value.map(toHumanText).filter(Boolean).slice(0, 6).join(', ');
	return toHumanText(value);
};

const toHumanText = (value: unknown): string => {
	if (typeof value === 'string') return value.replace(/\s+/g, ' ').trim();
	if (typeof value === 'number' || typeof value === 'boolean') return String(value);
	if (Array.isArray(value)) return value.map(toHumanText).filter(Boolean).slice(0, 5).join(', ');
	if (value && typeof value === 'object') {
		const record = value as Record<string, unknown>;
		for (const key of [
			'style_description',
			'summary',
			'label',
			'title',
			'tone',
			'voice',
			'description',
			'segments'
		]) {
			const text = toHumanText(record[key]);
			if (text) return text;
		}
	}
	return '';
};

const compact = (value: unknown, fallback: string, maxLength = 180) => {
	const text = sanitizeOnboardingRuntimeMarker(toHumanText(value))
		.replace(
			/\b(?:deterministic|fallback|heuristic|metadata_json|keyword_|post_stats|topic_hints)\b/gi,
			''
		)
		.replace(/\s+/g, ' ')
		.trim();
	if (!text) return fallback;
	if (text.length <= maxLength) return text;
	const candidate = text.slice(0, maxLength + 1);
	const sentenceEnds = Array.from(candidate.matchAll(/[.!?](?=\s|$)/g));
	const lastSentenceEnd = sentenceEnds.at(-1)?.index;
	if (lastSentenceEnd !== undefined && lastSentenceEnd >= Math.floor(maxLength * 0.55)) {
		return candidate.slice(0, lastSentenceEnd + 1).trim();
	}
	const lastWordBoundary = candidate.lastIndexOf(' ');
	return `${candidate.slice(0, lastWordBoundary > 0 ? lastWordBoundary : maxLength).trim()}…`;
};

const humanizeBrandInsight = (value: unknown) => {
	const text = toHumanText(value).replace(/^вероятная аудитория:\s*/i, '');
	return text
		.split(/(?<=[.!?])\s+/)
		.filter(
			(sentence) =>
				!/(?:профиль\s+предварительн|корпус\s+(?:почти|состоит)|уровень\s+уверенности|данных\s+(?:пока\s+)?мало|доступен\s+только\s+(?:один|одиноч))/i.test(
					sentence
				)
		)
		.join(' ')
		.trim();
};

const toneToRussian = (tone: unknown) => {
	const dictionary: Record<string, string> = {
		clear: 'понятный',
		human: 'живой',
		warm: 'тёплый',
		soft: 'мягкий',
		expert: 'экспертный',
		trust: 'с акцентом на доверие',
		spiritual: 'со смыслом'
	};
	if (!Array.isArray(tone)) return '';
	return tone
		.map((item) => dictionary[String(item)] ?? String(item))
		.filter(Boolean)
		.join(', ');
};

const firstDiscoveryFinding = (findings: BrandDiscoveryFinding[], category: string) =>
	findings.find((finding) => finding.category === category)?.inferred_value_json;

const discoveryGroundingText = (
	run: BrandDiscoveryRun,
	findings: BrandDiscoveryFinding[],
	sourceText: string
) => {
	const report = (run?.report_json ?? {}) as Record<string, any>;
	const evidence = findings.flatMap((finding) =>
		(finding.evidence_json ?? []).flatMap((item) => [item.preview, item.reason].map(toHumanText))
	);
	return [
		sourceText,
		toHumanText(report.brand_profile?.business_description),
		toHumanText(report.business_memory?.offering),
		toHumanText(report.business_memory?.audience),
		toHumanText(report.business_memory?.customer_job),
		...findings.map((finding) => toHumanText(finding.inferred_value_json)),
		...evidence
	]
		.filter(Boolean)
		.join(' ');
};

const discoveryOffering = (run: BrandDiscoveryRun, findings: BrandDiscoveryFinding[]) => {
	const report = (run?.report_json ?? {}) as Record<string, any>;
	const raw =
		toHumanText(firstDiscoveryFinding(findings, 'brand_profile')) ||
		toHumanText(report.brand_profile?.business_description);
	if (!raw) return '';
	const websiteTitle = raw.match(/(?:^|\s)Title:\s*(.*?)(?=\s+Description:|$)/iu)?.[1]?.trim();
	const websiteDescription = raw.match(/(?:^|\s)Description:\s*(.*)$/iu)?.[1]?.trim();
	const clean = (websiteTitle || websiteDescription || raw)
		.replace(/^On-API\s+onboarding\s+website\s+analysis\s*/iu, '')
		.replace(/^URL:\s*https?:\/\/\S+\s*/iu, '')
		.trim();
	return compact(clean, '', 150);
};

const sourceDerivedThemes = (offering: string) =>
	offering ? `${offering}; как всё проходит на практике; условия, детали и ответы на вопросы` : '';

const firstVoiceFinding = (findings: BrandVoiceFinding[], categories: string[]) => {
	for (const finding of findings) {
		if (!categories.includes(finding.category)) continue;
		const payload = finding.edited_content_json ?? finding.content_json ?? {};
		const text = toHumanText(payload) || toHumanText(finding.summary) || toHumanText(finding.title);
		if (text) return text;
	}
	return '';
};

const voiceFindingTexts = (findings: BrandVoiceFinding[], categories: string[], limit = 5) =>
	unique(
		findings
			.filter((finding) => categories.includes(finding.category))
			.map((finding) => {
				const payload = finding.edited_content_json ?? finding.content_json ?? {};
				return toHumanText(payload) || toHumanText(finding.summary) || toHumanText(finding.title);
			})
			.filter(Boolean)
	).slice(0, limit);

const firstVoiceFindingPayload = (
	findings: BrandVoiceFinding[],
	category: string
): Record<string, unknown> => {
	const finding = findings.find((item) => item.category === category);
	return (
		((finding?.edited_content_json ?? finding?.content_json ?? {}) as Record<string, unknown>) ?? {}
	);
};

const humanizeVoicePolicy = (value: unknown) => {
	const text = toHumanText(value);
	const translations: Array<[RegExp, string]> = [
		[
			/^clear hook, useful body, and a soft ending\.?$/i,
			'ясный заход, содержательная основная часть и мягкое завершение'
		],
		[/^use a soft, low-pressure cta\.?$/i, 'мягкий призыв без давления'],
		[
			/^sell through usefulness, context, and calm explanation\.?$/i,
			'продажа через пользу, контекст и спокойное объяснение'
		],
		[
			/^use concrete observations and light story framing when relevant\.?$/i,
			'конкретные наблюдения и лёгкая история вместо сухого перечисления'
		]
	];
	return translations.find(([pattern]) => pattern.test(text))?.[1] ?? text;
};

const unresolvedAudience =
	'Аудитория пока не определена — уточню её по фактам о проекте и первым откликам';

const isGenericThemesLabel = (value: string) => {
	const labels = value
		.split(/[;,]/)
		.map((item) => item.trim())
		.filter(Boolean);
	return (
		labels.length > 0 &&
		labels.every((item) => /^(?:topic|theme|content pillar|тема|рубрика)\s*\d+$/i.test(item))
	);
};

const onboarding1AnalysisEvidence = (run: BrandDiscoveryRun) => {
	const input = (run?.input_summary_json ?? {}) as Record<string, unknown>;
	const sourceAnalysis = (input.source_analysis ?? {}) as Record<string, unknown>;
	const postCount = Number(sourceAnalysis.post_count ?? 0);
	const platforms = Array.isArray(sourceAnalysis.platforms)
		? sourceAnalysis.platforms
				.map(String)
				.filter((platform) => platform && platform !== 'text')
				.map((platform) => (platform.toLowerCase() === 'telegram' ? 'Telegram' : platform))
		: [];
	if (postCount > 0) {
		return `${postCount} публикаций${platforms.length ? ` в ${unique(platforms).join(', ')}` : ''}`;
	}
	return 'ваше описание идеи';
};

const onboarding1SourceIdentity = (run: BrandDiscoveryRun, sourceText: string) => {
	const input = (run?.input_summary_json ?? {}) as Record<string, any>;
	const sources = Array.isArray(input.source_analysis?.sources)
		? (input.source_analysis.sources as Record<string, unknown>[])
		: [];
	const sourceUrl =
		toHumanText(sources.find((item) => toHumanText(item.url))?.url) ||
		sourceText.match(/https?:\/\/[^\s)\]}>,]+/i)?.[0] ||
		'';
	const sourceTitle = toHumanText(sources.find((item) => toHumanText(item.title))?.title);
	if (!sourceUrl) return { sourceLabel: sourceTitle, sourceUrl: '' };
	try {
		const hostname = new URL(sourceUrl).hostname.replace(/^www\./i, '');
		return { sourceLabel: hostname || sourceTitle || sourceUrl, sourceUrl };
	} catch {
		return { sourceLabel: sourceTitle || sourceUrl, sourceUrl };
	}
};

const cleanAudienceSummary = (value: string) =>
	value
		.replace(/;\s*основной запрос\s*—[\s\S]*$/i, '')
		.replace(/^люди, которым актуально предложение:\s*/i, '')
		.trim();

const hasUnresolvedFact = (value: string) =>
	/(?:пока\s+(?:не\s+)?(?:определ|нужно|неизвест)|нужно\s+(?:уточнить|подтвердить)|недостаточно)/i.test(
		value
	);

const semanticFactTokens = (value: string) =>
	(value.toLocaleLowerCase('ru-RU').match(/[a-zа-яё0-9-]{3,}/giu) ?? []).filter(
		(token) =>
			!/^(?:это|этот|эта|эти|пока|сейчас|раньше|котор|бренд|проект|контент|клиент|аудитор|нужно|хочу)$/iu.test(
				token
			)
	);

const isCompleteSemanticFact = (value: unknown) => {
	const text = toHumanText(value).replace(/\s+/g, ' ').trim();
	if (!text || hasUnresolvedFact(text)) return false;
	if (semanticFactTokens(text).length < 2) return false;
	if (/^(?:пока|сейчас|раньше)\b/iu.test(text)) return false;
	if (/(?:^|\s)(?:и|или|а|но|для|по|с|со|к|ко|о|об|от|до|на|в|во|из|за)$/iu.test(text)) {
		return false;
	}
	return true;
};

const isOwnerIdentityOnly = (value: unknown) =>
	/^(?:я|мы)\s+(?:основател|владел|автор|создател|команда|предпринимател)/iu.test(
		toHumanText(value).trim()
	);

const isMetaCorrectionInstruction = (value: unknown) =>
	/^(?:неверн\w*|неправильн\w*|ошиб\w*|исправ\w*|уточн\w*)\s+(?:поле\b|вывод\b|формулировк\w*\b|ответ\b)/iu.test(
		toHumanText(value).trim()
	);

const isOnboardingTaskEcho = (value: unknown) =>
	/^(?:первая|первый|основная|основной)\s+(?:задача|цель)(?:\s+для\s+контента)?\s*[:—-]/iu.test(
		toHumanText(value).trim()
	);

const isOnboardingContentGoalEcho = (value: unknown, contentGoal: unknown) => {
	const candidate = toHumanText(value).trim();
	const goal = toHumanText(contentGoal).trim();
	if (!candidate || !goal) return false;
	const goalTokens = new Set(semanticFactTokens(goal));
	const overlap = semanticFactTokens(candidate).filter((token) => goalTokens.has(token)).length;
	return (
		overlap >= Math.min(2, goalTokens.size) &&
		/(?:публикац|соцсет|пост(?:ы|а|ов)?|контент)/iu.test(candidate)
	);
};

const isStronglySourceGrounded = (value: unknown, sourceText: string) => {
	const candidateTokens = unique(semanticFactTokens(toHumanText(value)));
	if (!candidateTokens.length || !sourceText.trim()) return false;
	const sourceTokens = new Set(semanticFactTokens(sourceText));
	const overlap = candidateTokens.filter((token) => sourceTokens.has(token)).length;
	return overlap >= Math.min(2, candidateTokens.length);
};

export const hasResolvedOnboarding1BrandFact = (value: unknown) => {
	const text = toHumanText(value).trim();
	return Boolean(text) && !hasUnresolvedFact(text);
};

const lowerFirst = (value: string) =>
	value ? `${value.charAt(0).toLocaleLowerCase('ru-RU')}${value.slice(1)}` : '';

const upperFirst = (value: string) =>
	value ? `${value.charAt(0).toLocaleUpperCase('ru-RU')}${value.slice(1)}` : '';

export const cleanOnboarding1SummarySentence = (value: string) => {
	const original = value.trim();
	if (/…+$/u.test(original)) {
		const withoutEllipsis = original.replace(/…+$/u, '').trim();
		const completeEnds = Array.from(withoutEllipsis.matchAll(/[.!?](?=\s|$)/gu));
		const lastCompleteEnd = completeEnds.at(-1)?.index;
		if (lastCompleteEnd !== undefined) {
			return withoutEllipsis
				.slice(0, lastCompleteEnd + 1)
				.replace(/[.!?]+$/u, '')
				.trim();
		}
	}
	let cleaned = original
		.trim()
		.replace(/[.!?…]+$/, '')
		.replace(/[,;:—-]+\s*$/, '')
		.replace(/\s+(?:в|во|на|за|из|к|ко|с|со|у|о|об|от|до|для|по|при|и|или|а|но)$/i, '')
		.trim();
	if (
		/(?:(?:^|\s)(?:после\s+этого|затем|потом)\s+)?(?:я|мы|вы|он|она|они|это|котор(?:ый|ая|ое|ые)|могу|можем|буду|будем|должен|должна|должны)$/iu.test(
			cleaned
		)
	) {
		cleaned = cleaned.replace(/[,;:—-]\s*[^,;:—-]*$/u, '').trim();
	}
	return cleaned;
};

export const conciseOnboarding1Fact = (value: unknown, maxLength = 96) => {
	const text = toHumanText(value).replace(/\s+/g, ' ').trim();
	if (!text) return '';
	const firstSentenceEnd = text.search(/[.!?](?=\s|$)/u);
	const firstSentence = firstSentenceEnd >= 0 ? text.slice(0, firstSentenceEnd + 1) : '';
	const candidate = firstSentence.length >= 24 ? firstSentence : text;
	return upperFirst(compact(candidate, '', maxLength).replace(/[.!?…]+$/u, ''));
};

const sentence = cleanOnboarding1SummarySentence;

const stripDanglingCalibrationWords = (value: string) =>
	value
		.replace(/[\s,;:—-]+$/u, '')
		.replace(/\s+(?:и|или|а|но|с|со|без|для|к|ко|о|об|от|до|из|на|по|при|через)$/iu, '')
		.replace(/[\s,;:—-]+$/u, '')
		.trim();

const fitCompleteCalibrationWords = (value: string, maxLength: number) => {
	const normalized = toHumanText(value).replace(/\s+/g, ' ').trim();
	if (!normalized) return '';
	const hadTrailingEllipsis = /(?:…|\.\.\.)$/u.test(normalized);
	let clean = normalized.replace(/(?:…|\.\.\.)+$/u, '').trim();
	if (hadTrailingEllipsis) clean = clean.replace(/\s+\S+$/u, '').trim();
	if (clean.length > maxLength) {
		const candidate = clean.slice(0, maxLength + 1);
		const wordBoundary = candidate.lastIndexOf(' ');
		clean = candidate.slice(0, wordBoundary > 0 ? wordBoundary : maxLength).trim();
	}
	return stripDanglingCalibrationWords(clean);
};

export const fitOnboarding1CalibrationTitle = (value: unknown) => {
	const raw = humanizeBrandInsight(value)
		.split(/(?<=[.!?])\s+|[;:]/)[0]
		?.replace(/^(?:подача|стиль|голос|тон)\s*[:—-]?\s*/iu, '')
		.trim();
	if (!raw) return '';

	const words = raw.split(/\s+/u);
	const fitted: string[] = [];
	let line = 1;
	let lineLength = 0;
	for (const word of words) {
		const nextLength = lineLength ? lineLength + 1 + word.length : word.length;
		if (nextLength <= 22) {
			fitted.push(word);
			lineLength = nextLength;
			continue;
		}
		if (line === 1) {
			line = 2;
			lineLength = word.length;
			fitted.push(word);
			continue;
		}
		break;
	}

	const complete = fitCompleteCalibrationWords(fitted.join(' '), 46);
	if (complete.length < 4) return '';
	return upperFirst(complete.replace(/[.!?…]+$/u, ''));
};

const fitCalibrationText = (value: string) => {
	const normalized = toHumanText(value).replace(/\s+/g, ' ').trim();
	if (!normalized) return '';
	return fitCompleteCalibrationWords(normalized, 210)
		.replace(/[.!?]+$/u, '')
		.trim();
};

const isTechnicalCalibrationBasis = (value: string) =>
	/^(?:(?:source|post|message|chunk|block)[\s:_-]*)?[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}(?::[\w-]+)*$/i.test(
		value.trim()
	) ||
	/^(?:source|post|message|chunk|block)(?::[\w-]+){1,}$/i.test(value.trim()) ||
	/^https?:\/\//i.test(value.trim());

export const displayOnboarding1CalibrationBasis = (value: unknown, fallback = '') => {
	const text = toHumanText(value)
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^(?:основа|что\s+предлага(?:ете|ем|ет)|продукт|услуга|предложение)\s*[:—-]\s*/iu, '')
		.trim();
	if (!text || isTechnicalCalibrationBasis(text)) return fallback;
	return text;
};

const isGenericCalibrationTitle = (value: string) =>
	/^(?:(?:первый|второй)\s+вариант|(?:вариант|подача|стиль|голос|тон)\s*(?:(?:№|номер)\s*)?[12]?)$/iu.test(
		value
			.toLocaleLowerCase('ru-RU')
			.replace(/[.!?…:;—-]+$/u, '')
			.trim()
	);

const isGenericCalibrationProfileSummary = (value: unknown) => {
	const text = toHumanText(value).trim();
	return (
		!text ||
		/^подача\s+по\s+материалам\s+бренда[.!]?$/iu.test(text) ||
		/^brand\s+voice\s+profile\s+from\s+llm\s+analysis[.!]?$/iu.test(text)
	);
};

const isGenericOnboarding1Style = (value: unknown) =>
	isGenericCalibrationProfileSummary(value) ||
	/^(?:общ(?:ий|ая)\s+)?(?:стиль|тон|голос)\s+бренда[.!]?$/iu.test(toHumanText(value).trim());

const normalizedCalibrationIdentity = (value: string) =>
	value
		.toLocaleLowerCase('ru-RU')
		.replace(/[^\p{L}\p{N}]+/gu, ' ')
		.replace(/\s+/g, ' ')
		.trim();

const generatedCalibrationCopy = (item: Record<string, unknown>) => {
	const rawBody = String(item.body ?? item.text ?? '').trim();
	const rawTitle = humanizeBrandInsight(item.title)
		.replace(/^(?:подача|стиль|голос|тон)\s*[:—-]?\s*/iu, '')
		.replace(/[.!?…]+$/u, '')
		.trim();
	const bodyLines = rawBody
		.split(/\r?\n+/)
		.map((line) => toHumanText(line).replace(/\s+/g, ' ').trim())
		.filter(Boolean);
	let title = calibrationDirectionTitle(item.title);
	let body = toHumanText(rawBody).replace(/\s+/g, ' ').trim();

	if (isGenericCalibrationTitle(title)) {
		const inlineHeadingMatch =
			bodyLines.length < 2
				? body.match(/^(.{18,72}?[^\s.!?…])\s+(?=[А-ЯЁ](?:[а-яё]+|\s+[а-яё]{2,})(?:\s|$))/u)
				: null;
		const promotedTitle = calibrationDirectionTitle(
			bodyLines.length >= 2 ? bodyLines[0] : inlineHeadingMatch?.[1]
		);
		const promotedBody =
			bodyLines.length >= 2
				? bodyLines.slice(1).join(' ').trim()
				: inlineHeadingMatch
					? body.slice(inlineHeadingMatch[1].length).trim()
					: '';
		if (!promotedTitle || promotedBody.length < 55) return null;
		title = promotedTitle;
		body = promotedBody;
	}

	const normalizedTitle = normalizedCalibrationIdentity(title);
	const normalizedBody = normalizedCalibrationIdentity(body);
	const duplicateTitle =
		rawTitle && normalizedBody.startsWith(`${normalizedCalibrationIdentity(rawTitle)} `)
			? rawTitle
			: normalizedTitle && normalizedBody.startsWith(`${normalizedTitle} `)
				? title
				: '';
	if (duplicateTitle) {
		const duplicatePrefix = body.slice(0, duplicateTitle.length).trim();
		const bodyWithoutDuplicateTitle = body
			.slice(duplicatePrefix.length)
			.replace(/^[\s.!?…:;—-]+/u, '')
			.trim();
		if (bodyWithoutDuplicateTitle.length >= 55) body = bodyWithoutDuplicateTitle;
	}

	body = fitCalibrationText(body);
	if (body.length < 24 || !title || isGenericCalibrationTitle(title)) return null;
	return { title, body };
};

const calibrationDirectionTitle = (value: unknown) => {
	return fitOnboarding1CalibrationTitle(value);
};

const calibrationDirectionTitles = (
	voiceProfilePayload: Record<string, unknown>,
	style: string,
	delivery: string
) => {
	const traits = Array.isArray(voiceProfilePayload.voice_traits)
		? (voiceProfilePayload.voice_traits as Array<Record<string, unknown> | string>).map((item) =>
				typeof item === 'string' ? item : (item.label ?? item.description)
			)
		: [];
	return unique(
		[
			...traits,
			voiceProfilePayload.sentence_style,
			voiceProfilePayload.storytelling_policy,
			voiceProfilePayload.cta_policy,
			voiceProfilePayload.emotional_register,
			style,
			delivery
		]
			.map(calibrationDirectionTitle)
			.filter(Boolean)
	).slice(0, 2);
};

const buildOnboarding1CalibrationVariants = ({
	offering,
	audience,
	customerJob,
	trustSignals,
	style,
	delivery,
	themes,
	voiceProfilePayload
}: {
	offering: string;
	audience: string;
	customerJob: string;
	trustSignals: string;
	style: string;
	delivery: string;
	themes: string;
	voiceProfilePayload: Record<string, unknown>;
}): Onboarding1CalibrationVariant[] => {
	const generated = Array.isArray(voiceProfilePayload.calibration_examples)
		? (voiceProfilePayload.calibration_examples as Record<string, unknown>[])
		: [];
	const generatedVariants = generated
		.slice(0, 2)
		.map((item, index): Onboarding1CalibrationVariant | null => {
			const copy = generatedCalibrationCopy(item);
			if (!copy) return null;
			const { body, title } = copy;
			const id: Onboarding1CalibrationVariantId = index === 0 ? 'variant_1' : 'variant_2';
			const rawProfileSummary = item.profile_summary;
			return {
				id,
				title,
				body,
				basis: compact(
					displayOnboarding1CalibrationBasis(item.basis, offering || themes),
					offering || themes,
					88
				),
				profileSummary: compact(
					isGenericCalibrationProfileSummary(rawProfileSummary)
						? `${title}. ${style}`
						: rawProfileSummary,
					`${title}. ${style}`,
					260
				),
				tone: Array.isArray(item.tone) ? item.tone.map(String).filter(Boolean) : []
			};
		})
		.filter((item): item is Onboarding1CalibrationVariant => Boolean(item));
	const generatedDirectionsAreDistinct =
		generatedVariants.length === 2 &&
		normalizedCalibrationIdentity(generatedVariants[0].title) !==
			normalizedCalibrationIdentity(generatedVariants[1].title) &&
		normalizedCalibrationIdentity(generatedVariants[0].body) !==
			normalizedCalibrationIdentity(generatedVariants[1].body);
	if (generatedDirectionsAreDistinct) {
		const sharedBasis = compact(
			generatedVariants[0].basis || generatedVariants[1].basis,
			offering || themes,
			88
		);
		return generatedVariants.map((variant) => ({
			...variant,
			basis: sharedBasis
		}));
	}

	const concreteOffering = sentence(offering || themes.split(';')[0] || 'Предложение бренда');
	const concreteAudience = hasUnresolvedFact(audience) ? '' : sentence(audience);
	const audiencePreferenceMatch = concreteAudience.match(
		/^(?<subject>.+?),\s*которые\s+выбирают\s+(?<choice>.+?)\s+и\s+ценят\s+(?<value>.+)$/iu
	);
	const audiencePreference = audiencePreferenceMatch?.groups
		? {
				direct: upperFirst(
					`${audiencePreferenceMatch.groups.subject} выбирают ${audiencePreferenceMatch.groups.choice} и ценят ${audiencePreferenceMatch.groups.value}`
				),
				alternative: `${upperFirst(audiencePreferenceMatch.groups.choice)} и ${audiencePreferenceMatch.groups.value} — выбор ${lowerFirst(audiencePreferenceMatch.groups.subject).replace(/^люди(?=\s|$)/iu, 'людей')}`
			}
		: null;
	const offerWithAudience = upperFirst(concreteOffering);
	const basis = compact(concreteOffering || themes, 'факты из ваших материалов', 88);
	const titles = calibrationDirectionTitles(voiceProfilePayload, style, delivery);
	const firstTitle = titles[0] || calibrationDirectionTitle(style) || 'Прямо и по делу';
	const secondTitle =
		titles.find((title) => title !== firstTitle) ||
		calibrationDirectionTitle(delivery) ||
		'Через ситуацию и ощущение';
	const discoveredTone = Array.isArray(voiceProfilePayload.tone)
		? voiceProfilePayload.tone.map(String).filter(Boolean)
		: [];
	return [
		{
			id: 'variant_1',
			title: firstTitle,
			body: fitCalibrationText(
				trustSignals
					? [offerWithAudience, sentence(trustSignals)].filter(Boolean).join('. ')
					: audiencePreference?.direct || `В центре — ${lowerFirst(offerWithAudience)}`
			),
			basis,
			profileSummary: compact(style, firstTitle, 260),
			tone: discoveredTone
		},
		{
			id: 'variant_2',
			title: secondTitle,
			body: fitCalibrationText(
				customerJob
					? `${upperFirst(sentence(customerJob))}. ${offerWithAudience}`
					: audiencePreference?.alternative || `Без лишних слов: ${lowerFirst(offerWithAudience)}`
			),
			basis,
			profileSummary: compact(
				voiceProfilePayload.storytelling_policy || delivery,
				secondTitle,
				260
			),
			tone: discoveredTone
		}
	];
};

const isGenericDeliveryLabel = (value: string) =>
	/^(?:platform\s+)?style\s+rule\s*\d*$/i.test(value.trim()) ||
	/^(?:telegram|vk|threads|max)\s+platform\s+rule$/i.test(value.trim());

const voiceDelivery = (findings: BrandVoiceFinding[]) => {
	const finding = findings.find((item) => item.category === 'platform_rule');
	if (!finding) return firstVoiceFinding(findings, ['post_type_template', 'do_dont']);
	const payload = (finding.edited_content_json ?? finding.content_json ?? {}) as Record<
		string,
		unknown
	>;
	const primary = [payload.style_description, finding.summary]
		.map(toHumanText)
		.find((value) => value && !isGenericDeliveryLabel(value));
	if (primary) return primary;
	return [payload.structure_policy, payload.post_length_policy, payload.cta_policy]
		.map(toHumanText)
		.filter((value) => value && !isGenericDeliveryLabel(value))
		.slice(0, 3)
		.join('; ');
};

const isGenericAudienceLabel = (value: string) =>
	/^(?:контент[- ]?)?аудитория бренда$/i.test(value.trim()) ||
	/^(?:целевая|основная) аудитория$/i.test(value.trim());

const sourceDeclaresAudienceUnknown = (sourceText: string) => {
	const normalized = sourceText.toLowerCase().replace(/\s+/g, ' ').trim();
	if (!normalized) return false;
	const unknownSignal =
		'(?:не\\s+зна(?:ю|ем)|не\\s+определен[аоы]?|пока\\s+не\\s+(?:зна(?:ю|ем)|понятн[ао])|не\\s+уверен[аы]?)';
	const audienceSignal = '(?:аудитори(?:я|ю|и)|для\\s+кого|клиент(?:ы|ов)?|покупател(?:и|ей)?)';
	return (
		new RegExp(`${audienceSignal}.{0,56}${unknownSignal}`, 'i').test(normalized) ||
		new RegExp(`${unknownSignal}.{0,56}${audienceSignal}`, 'i').test(normalized)
	);
};

const sourceSentence = (sourceText: string, pattern: RegExp) => {
	const match = sourceText.replace(/\s+/g, ' ').trim().match(pattern);
	return match?.[1]?.trim().replace(/[;,]+$/, '') ?? '';
};

/**
 * Split the business description from the requested content outcome.
 *
 * A cold-start brief is often one sentence ("Делаю X и хочу Y"). Keeping
 * that sentence in both fields makes the facts review echo the user's words
 * instead of explaining the business. The business offer remains the source
 * of truth; the second clause is the separate content goal.
 */
const splitOnboarding1OfferAndGoal = (value: string) => {
	const normalized = value.replace(/\s+/g, ' ').trim();
	if (!normalized) return { offering: '', goal: '' };
	const match = normalized.match(
		/\s+(?:и\s+)?(?:я\s+)?(?<goal>хочу|хотим|нужно|надо|планирую|планируем)\s+(?<rest>.+)$/iu
	);
	if (!match?.groups?.goal || !match.groups.rest || match.index === undefined) {
		return { offering: normalized, goal: '' };
	}
	const prefix = normalized
		.slice(0, match.index)
		.replace(/\s+и\s*$/iu, '')
		.trim();
	if (!prefix) return { offering: normalized, goal: '' };
	return {
		offering: prefix.replace(/[.!?]+$/u, ''),
		goal: `${match.groups.goal} ${match.groups.rest}`.trim().replace(/[.!?]+$/u, '')
	};
};

const normalizeOnboarding1ContentGoal = (value: unknown, offering: string) => {
	const raw = sanitizeOnboardingRuntimeMarker(toHumanText(value));
	if (!raw) return '';
	const split = splitOnboarding1OfferAndGoal(raw);
	if (split.goal && split.offering) return upperFirst(split.goal);
	if (offering && raw.toLocaleLowerCase('ru-RU') === offering.toLocaleLowerCase('ru-RU')) return '';
	return raw;
};

export const explicitBrandSeed = (sourceText: string) => {
	const labeledAudience = sourceSentence(
		sourceText,
		/(?:(?:исправ(?:ь|ьте)|измен(?:и|ите)|уточн(?:и|ите))\s+)?(?:(?:(?:наша|целевая|основная)\s+)?аудитори(?:я|ю|и)|(?:(?:наши|основные|целевые)\s+)?(?:покупатели|клиенты))\s*(?:—|-|:|это)\s*([^.!?\n]{3,260})/i
	);
	const describedAudience = sourceSentence(
		sourceText,
		/(?:^|\s)для\s+((?:(?:[а-яё-]{3,})\s+){0,3}(?:людей|взрослых|детей|родителей|клиентов|покупателей|подписчиков|компаний|команд|специалистов|предпринимателей)(?:\s|,)[^.!?\n]{3,220})/i
	);
	const objectAudience = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)(?:(?:это|наше|наш|этот|эта)\s+)?(?:решени[еяю]|продукт[а-яё-]*|сервис[а-яё-]*|услуг[ауи])\s+(?:обычно\s+|часто\s+)?(?:покупают|выбирают|заказывают)\s+([^.!?\n]{3,180})/iu
	);
	const behaviorAudienceCandidate = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)([^,.!?]{3,100}?)\s+(?:(?:обычно|часто)\s+)?(?:приходят|обращаются|покупают|заказывают|выбирают)(?=\s|,)/iu
	).replace(/\s+(?:обычно|часто)$/iu, '');
	const normalizedBehaviorAudienceCandidate = behaviorAudienceCandidate
		.replace(/^(?:сейчас|пока|раньше|в\s+основном|обычно|часто)\s+/iu, '')
		.trim();
	const behaviorAudience =
		/^(?:я|мы|ко?\s+(?:мне|нам)|к\s+нам|у\s+нас|клиенты|покупатели|участники|заказчики|гости|подписчики)$/iu.test(
			normalizedBehaviorAudienceCandidate
		)
			? ''
			: normalizedBehaviorAudienceCandidate;
	const behaviorAudienceAfterVerb = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)(?:(?:в\s+основном|обычно|часто)\s+)?(?:ко?\s+(?:мне|нам)|у\s+нас)\s+(?:часто\s+|обычно\s+)?(?:приходят|обращаются|покупают|заказывают|выбирают)\s+([^,:.!?]{3,100}?)(?=:|,|\s+котор(?:ым|ые|ая|ый)\b|[.!?])/iu
	);
	const audience =
		labeledAudience ||
		describedAudience ||
		objectAudience ||
		behaviorAudience ||
		behaviorAudienceAfterVerb;
	const customerJobByWhen = sourceSentence(
		sourceText,
		/(?:приходят|обращаются|покупают|заказывают|выбирают)[^.!?\n]{0,100}?когда\s+([^.!?\n]{3,220})/iu
	);
	const customerJobByRelativeClause = sourceSentence(
		sourceText,
		/(?:приходят|обращаются|покупают|заказывают|выбирают)[^.!?\n]{3,140}?,?\s+котор(?:ым|ые|ая|ый)\s+(?:важно|нужно|хочется|требуется)\s+([^.!?\n]{3,220})/iu
	);
	const customerJob = customerJobByWhen || customerJobByRelativeClause;
	const labeledOffering = sourceSentence(
		sourceText,
		/(?:(?:что\s+предлага(?:ем|ете)|продукт|услуга|предложение|описание\s+проекта|идея)\s*(?:—|-|:|это)\s*)([^.!?\n]{3,220})/iu
	);
	const actionOffering = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)(?:(?:я|мы)\s+)?(?:предлага(?:ю|ем)|сда(?:ю|ём|ем)|прода(?:ю|ём|ем)|дела(?:ю|ем)|созда(?:ю|ём|ем)|провожу|проводим|организую|организуем|помогаю|помогаем|внедря(?:ю|ем))\s+([^.!?\n]{3,220})/iu
	);
	const serviceOffering = sourceSentence(
		sourceText,
		/(?:^|[.!?:;]\s+)((?:заняти[а-яё-]*|консультаци[а-яё-]*|услуг[а-яё-]*|курс[а-яё-]*|обучени[а-яё-]*|программ[а-яё-]*|сервис[а-яё-]*|продукт[а-яё-]*|товар[а-яё-]*|аренд[а-яё-]*|доставк[а-яё-]*|прогулк[а-яё-]*|съ[её]мк[а-яё-]*|мастер-класс[а-яё-]*)\s+[^.!?\n]{3,220})/iu
	);
	const possessionOffering = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)(?:у\s+меня|у\s+нас)\s+(?!есть\s+(?:сайт|ссылка|канал|страниц))([^.!?\n]{3,220})/iu
	);
	const projectOffering = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)((?:у\s+нас\s+)?(?:(?:небольшой|локальный|новый|семейный|авторский)\s+)?(?:бренд|проект|сервис|магазин|мастерская|студия)\s+[^.!?\n]{3,180})/i
	);
	const rawOffering = labeledOffering
		? labeledOffering.replace(/^(?:у\s+меня|у\s+нас)\s+/iu, '').trim()
		: serviceOffering || actionOffering || projectOffering || possessionOffering;
	const splitOffering = splitOnboarding1OfferAndGoal(rawOffering);
	const offering = splitOffering.offering || rawOffering;
	const statedTone = sourceSentence(
		sourceText,
		/(?:^|[.!?]\s+)(?:тон|стиль|подача)\s*(?:—|-|:|это)\s*([^.!?\n]{3,260})/iu
	);
	const styleStatement =
		statedTone ||
		sourceSentence(sourceText, /(?:хотим|должны|будем|нужно)\s+звучать\s+([^.!?\n]{3,260})/i);
	const [styleHead, ...styleTailParts] = styleStatement.split(':');
	const inlineDelivery = styleTailParts.join(':').trim();
	const style = (styleHead || styleStatement).trim();
	const themes = sourceSentence(
		sourceText,
		/(?:темы|рубрики|о\s+ч[её]м\s+(?:говорим|пишем))\s*(?:—|-|:|это)?\s*([^.!?\n]{3,320})/i
	);
	const standaloneDelivery = sourceSentence(
		sourceText,
		/((?:пишем|строим|пода[её]м|оформляем)\s+[^.!?\n]{3,280})/i
	);
	const delivery =
		standaloneDelivery ||
		(/(?:пример|абзац|структур|список|шаг|призыв|cta)/i.test(inlineDelivery) ? inlineDelivery : '');
	return { audience, offering, style, themes, delivery, customerJob };
};

export const resolveOnboarding1ReferenceScope = (sourceText: string): Onboarding1ReferenceScope => {
	if (!/https?:\/\/\S+/iu.test(sourceText)) return '';
	return /(?:референс|пример|ориентир)[^.!?\n]{0,220}(?:структур|подач|формат|тон|стил|голос|визуал)/iu.test(
		sourceText
	)
		? 'style_or_structure'
		: '';
};

export const resolveOnboarding1SourceRole = (
	sourceText: string
): 'brand_owned_social' | 'brand_style_reference' | '' => {
	if (!/https?:\/\/\S+/iu.test(sourceText)) return '';
	if (resolveOnboarding1ReferenceScope(sourceText)) return 'brand_style_reference';
	return /(?:это\s+мой|мой|свой|наш|веду|мы\s+вед[её]м)[^.!?\n]{0,100}(?:telegram|телеграм|тг|канал|профил)/iu.test(
		sourceText
	)
		? 'brand_owned_social'
		: '';
};

export const removeOnboarding1ScopedReferences = (sourceText: string): string => {
	if (!resolveOnboarding1ReferenceScope(sourceText)) return sourceText.trim();
	return sourceText
		.replace(/(?:референс|пример|ориентир)[^.!?\n]{0,260}https?:\/\/\S+/giu, '')
		.replace(/\s+/g, ' ')
		.trim();
};

const inferThemesFromBrandSeed = (sourceText: string) => {
	const offeringMatch = sourceText.match(
		/(?:делаем|созда[её]м|предлагаем|прода[её]м|разрабатываем|помогаем\s+с)\s+([^.!?\n]{3,220})/iu
	);
	const offering = (offeringMatch?.[1] ?? '')
		.split(/\s+для\s+(?:людей|клиентов|покупателей|подписчиков|компаний|команд)(?:\s|,)/iu)[0]
		.trim();
	const valueMatch = sourceText.match(/(?:важны|ценят|ищут|нужны)\s+([^.!?\n]{3,180})/iu);
	return unique([offering, valueMatch?.[1]?.trim() ?? '']).join('; ');
};

const isSourceGrounded = (candidate: string, sourceText: string) => {
	if (!candidate || !sourceText.trim()) return false;
	const source = sourceText.toLocaleLowerCase('ru-RU');
	const words = candidate
		.toLocaleLowerCase('ru-RU')
		.match(/[a-zа-яё0-9]{5,}/gi)
		?.filter((word) => !/^(?:котор|бренд|контент|польз|задач|аудитор)/.test(word));
	return Boolean(words?.some((word) => source.includes(word)));
};

export const buildOnboarding1BrandSummary = (
	run: BrandDiscoveryRun,
	discoveryFindings: BrandDiscoveryFinding[],
	voiceFindings: BrandVoiceFinding[],
	sourceText = '',
	calibrationAuthority: BrandVoiceCalibrationAuthorityV1 | null = null
): Onboarding1BrandSummary => {
	const referenceScope = resolveOnboarding1ReferenceScope(sourceText);
	const brandFactText = removeOnboarding1ScopedReferences(sourceText);
	const explicit = explicitBrandSeed(brandFactText);
	const groundingText = discoveryGroundingText(run, discoveryFindings, brandFactText);
	const offering = explicit.offering || discoveryOffering(run, discoveryFindings);
	const report = run?.report_json ?? {};
	const voice = (report.voice_profile ?? {}) as Record<string, unknown>;
	const patterns = (report.content_patterns ?? {}) as Record<string, unknown>;
	const audience = (report.audience_profile ?? {}) as Record<string, unknown>;
	const brand = (report.brand_profile ?? {}) as Record<string, unknown>;
	const businessMemory = (report.business_memory ?? {}) as Record<string, unknown>;
	const voiceProfilePayload = firstVoiceFindingPayload(voiceFindings, 'voice_profile');
	const businessProfilePayload = firstVoiceFindingPayload(voiceFindings, 'business_profile');
	const audienceProfilePayload = firstVoiceFindingPayload(voiceFindings, 'audience_profile');
	const businessOffering = toHumanText(businessProfilePayload.offering);
	const groundedBusinessOffering =
		isCompleteSemanticFact(businessOffering) &&
		!isOwnerIdentityOnly(businessOffering) &&
		isStronglySourceGrounded(businessOffering, groundingText)
			? businessOffering
			: '';
	const businessAudience = toHumanText(businessProfilePayload.audience);
	const groundedBusinessAudience =
		isCompleteSemanticFact(businessAudience) &&
		isStronglySourceGrounded(businessAudience, groundingText)
			? businessAudience
			: '';
	const deepVoiceProfile =
		humanizeBrandInsight(voiceProfilePayload.style_description) ||
		humanizeBrandInsight(voiceProfilePayload.summary) ||
		firstVoiceFinding(voiceFindings, ['voice_profile']);
	const style =
		explicit.style ||
		deepVoiceProfile ||
		toHumanText(voice.style_description) ||
		toneToRussian(voice.tone) ||
		toHumanText(firstDiscoveryFinding(discoveryFindings, 'voice')) ||
		firstVoiceFinding(voiceFindings, ['voice_profile', 'writing_rule']);
	const deepThemes = voiceFindingTexts(voiceFindings, ['topic_cluster']).join('; ');
	const themeCandidates = [
		deepThemes,
		readableList(patterns.content_pillars),
		toHumanText(firstDiscoveryFinding(discoveryFindings, 'content_pillar')),
		firstVoiceFinding(voiceFindings, ['topic_cluster'])
	];
	const generatedThemes = themeCandidates.find(
		(value, index) =>
			value &&
			!isGenericThemesLabel(value) &&
			(index === 0 ||
				index === themeCandidates.length - 1 ||
				isSourceGrounded(value, groundingText))
	);
	const seedThemes = referenceScope ? inferThemesFromBrandSeed(brandFactText) : '';
	const themes =
		explicit.themes ||
		seedThemes ||
		(deepThemes ? generatedThemes : '') ||
		sourceDerivedThemes(offering) ||
		generatedThemes ||
		'Темы пока не определены — предложу их после подтверждения фактов о проекте';
	const rawAudience =
		humanizeBrandInsight(audienceProfilePayload.summary) ||
		readableList(audienceProfilePayload.segments) ||
		readableList(audience.segments) ||
		toHumanText(firstDiscoveryFinding(discoveryFindings, 'audience'));
	const discoveryAudience = toHumanText(businessMemory.audience);
	const isAudienceFact = (value: string) =>
		isCompleteSemanticFact(value) &&
		!isGenericAudienceLabel(value) &&
		!/^(?:обращайтесь|пишите|говорите|используйте|рассказывайте|покажите|предлагайте)(?:\s|$)/iu.test(
			value.trim()
		);
	const audienceFinding = voiceFindings.find((finding) => finding.category === 'audience_profile');
	const hasGroundedAudienceFinding = Boolean(
		audienceFinding &&
		Number(audienceFinding.confidence ?? 0) >= 0.45 &&
		Array.isArray(audienceFinding.evidence_json) &&
		audienceFinding.evidence_json.length > 0
	);
	const audienceText = sourceDeclaresAudienceUnknown(brandFactText)
		? 'Аудитория пока не определена — уточним её по первым откликам, клиентам и материалам'
		: explicit.audience
			? explicit.audience
			: groundedBusinessAudience
				? groundedBusinessAudience
				: isAudienceFact(discoveryAudience)
					? discoveryAudience
					: rawAudience &&
						  isAudienceFact(rawAudience) &&
						  (hasGroundedAudienceFinding || isSourceGrounded(rawAudience, groundingText))
						? rawAudience
						: unresolvedAudience;
	const deepDelivery = [
		voiceProfilePayload.rhythm,
		voiceProfilePayload.cta_style,
		voiceProfilePayload.sales_style,
		voiceProfilePayload.storytelling_policy
	]
		.map(humanizeVoicePolicy)
		.filter(Boolean)
		.join('; ');
	const delivery =
		explicit.delivery ||
		deepDelivery ||
		toHumanText(voice.delivery) ||
		voiceDelivery(voiceFindings);
	const sourceIdentity = onboarding1SourceIdentity(run, sourceText);
	const sourceAnalysis = (run?.input_summary_json as Record<string, any>)?.source_analysis as
		| Record<string, unknown>
		| undefined;
	const sourcePlatforms = Array.isArray(sourceAnalysis?.platforms)
		? sourceAnalysis.platforms
				.map((platform: unknown) => String(platform).trim().toLowerCase())
				.filter(Boolean)
		: [];
	const sourceIsWebsite =
		Boolean(sourceIdentity.sourceUrl) &&
		!sourcePlatforms.some((platform: string) =>
			['telegram', 'instagram', 'threads', 'vk', 'max'].includes(platform)
		);
	const fallbackOffering = [offering, businessMemory.offering, brand.business_description]
		.map(toHumanText)
		.find(
			(candidate) =>
				isCompleteSemanticFact(candidate) &&
				!isOwnerIdentityOnly(candidate) &&
				isStronglySourceGrounded(candidate, groundingText)
		);
	const offeringText = compact(
		explicit.offering || groundedBusinessOffering || fallbackOffering || themes.split(';')[0],
		'Что именно вы предлагаете, пока нужно уточнить',
		Number.POSITIVE_INFINITY
	);
	const audienceDisplay = compact(
		cleanAudienceSummary(audienceText),
		'Кому подходит предложение, пока нужно уточнить',
		Number.POSITIVE_INFINITY
	);
	const normalizedContentGoal = normalizeOnboarding1ContentGoal(
		businessMemory.content_goal,
		offeringText
	);
	// A trailing ellipsis is an incomplete provider excerpt, not a fact the user
	// can approve. Hide it until a complete grounded goal is available instead of
	// presenting speculative filler such as “А ведь порой от нас…”.
	const contentGoal = /(?:…|\.\.\.)/u.test(normalizedContentGoal)
		? ''
		: compact(normalizedContentGoal, '', 150);
	const rawCustomerJob = compact(
		explicit.customerJob ||
			businessProfilePayload.customer_job ||
			businessMemory.customer_job ||
			readableList(audienceProfilePayload.decision_contexts) ||
			readableList(audienceProfilePayload.needs),
		'',
		Number.POSITIVE_INFINITY
	);
	const rawTrustSignals = compact(
		readableList(businessProfilePayload.trust_signals) ||
			readableList(businessMemory.trust_signals),
		'',
		125
	);
	const customerJob =
		isCompleteSemanticFact(rawCustomerJob) &&
		!isOwnerIdentityOnly(rawCustomerJob) &&
		!isOnboardingTaskEcho(rawCustomerJob) &&
		(Boolean(explicit.customerJob) || !isOnboardingContentGoalEcho(rawCustomerJob, contentGoal)) &&
		rawCustomerJob.toLocaleLowerCase('ru-RU') !== offeringText.toLocaleLowerCase('ru-RU')
			? rawCustomerJob
			: '';
	const trustSignals =
		isCompleteSemanticFact(rawTrustSignals) &&
		isStronglySourceGrounded(rawTrustSignals, groundingText)
			? rawTrustSignals
			: '';
	const publishingCadence = compact(businessMemory.publishing_cadence, '', 135);
	const workflowContext = compact(businessMemory.workflow_context, '', 135);
	const targetPlatforms = Array.isArray(businessMemory.target_platforms)
		? businessMemory.target_platforms.map(toHumanText).filter(Boolean)
		: [];
	const audienceConfidence = Number(
		audienceProfilePayload.confidence ?? audience.confidence ?? audienceFinding?.confidence ?? 0
	);
	const audienceNote = explicit.audience
		? ''
		: audienceConfidence >= 0.8 && !hasUnresolvedFact(audienceDisplay)
			? ''
			: sourceIdentity.sourceUrl
				? sourceIsWebsite
					? 'Предположение по сайту · нужно подтвердить'
					: 'Предположение по материалам · нужно подтвердить'
				: 'Предположение по материалам · нужно подтвердить';
	const evidenceItems = unique(
		[
			...discoveryFindings.flatMap((finding) =>
				(finding.evidence_json ?? []).map((item) => toHumanText(item.preview))
			),
			...voiceFindings.flatMap((finding) => {
				const evidence = Array.isArray(finding.evidence_json) ? finding.evidence_json : [];
				return evidence.map((item) => toHumanText(item?.quote ?? item?.preview));
			})
		]
			.map((item) => compact(item, '', 180))
			.filter(Boolean)
	).slice(0, 4);
	const calibrationVariants = calibrationAuthority
		? calibrationAuthority.choices.map(
				(choice, index): Onboarding1CalibrationVariant => ({
					id: index === 0 ? 'variant_1' : 'variant_2',
					choiceRef: choice.choice_ref,
					title: choice.title,
					body: choice.example_post,
					basis: choice.basis,
					profileSummary: choice.profile_summary,
					tone: [...choice.tone]
				})
			)
		: [];
	return {
		style: compact(style, 'живой, понятный и уверенный', 320),
		themes: compact(themes, 'польза, опыт бренда и задачи аудитории', 360),
		delivery: compact(delivery, 'без лишней официальности, с конкретикой и понятным выводом', 300),
		audience: audienceDisplay,
		evidence: onboarding1AnalysisEvidence(run),
		offering: offeringText,
		customerJob,
		trustSignals,
		contentGoal,
		publishingCadence,
		workflowContext,
		targetPlatforms,
		sourceLabel: sourceIdentity.sourceLabel,
		sourceUrl: sourceIdentity.sourceUrl,
		audienceNote,
		evidenceItems,
		calibrationVariants,
		calibrationChoiceSetRef: calibrationAuthority?.choice_set_ref
	};
};

const ONBOARDING1_BRAND_MEMORY_V2_MARKER = 'AEOLL_BRAND_MEMORY_V2';

export const normalizeOnboarding1BrandSummary = (
	summary: Onboarding1BrandSummary
): Onboarding1BrandSummary => {
	const repairedStyle = isGenericOnboarding1Style(summary.style)
		? summary.delivery || 'Спокойная содержательная подача'
		: summary.style;
	const hasServerAuthority =
		Boolean(summary.calibrationChoiceSetRef?.trim()) &&
		summary.calibrationVariants?.length === 2 &&
		summary.calibrationVariants.every((variant) => Boolean(variant.choiceRef?.trim()));
	const normalizedVariants = summary.calibrationVariants?.map((variant) => {
		if (hasServerAuthority) {
			return {
				...variant,
				title: variant.title.trim(),
				body: variant.body.trim(),
				basis: variant.basis.trim(),
				profileSummary: variant.profileSummary.trim(),
				tone: variant.tone.map((item) => item.trim()).filter(Boolean)
			};
		}
		const copy = generatedCalibrationCopy({
			title: variant.title,
			body: variant.body
		});
		const title = copy?.title || variant.title;
		return {
			...variant,
			title,
			body: copy?.body || fitCalibrationText(variant.body),
			basis: displayOnboarding1CalibrationBasis(variant.basis, summary.offering || summary.themes),
			profileSummary: isGenericCalibrationProfileSummary(variant.profileSummary)
				? compact(`${title}. ${repairedStyle}`, title, 260)
				: variant.profileSummary
		};
	});
	const repairedVariants = normalizedVariants?.map((variant, index, variants) => {
		if (
			index === 0 ||
			normalizedCalibrationIdentity(variant.title) !==
				normalizedCalibrationIdentity(variants[0].title)
		) {
			return variant;
		}
		const firstPersonTitle = /(?:^|\s)(?:я|мы|мне|нам|мой|моя|моё|наш|наша|наше)(?:\s|$)/iu.test(
			variant.body
		)
			? 'От первого лица'
			: '';
		const directionTitle = calibrationDirectionTitles({}, repairedStyle, summary.delivery).find(
			(title) =>
				normalizedCalibrationIdentity(title) !== normalizedCalibrationIdentity(variants[0].title)
		);
		return {
			...variant,
			title: firstPersonTitle || directionTitle || 'Через другую точку зрения'
		};
	});
	const variantsAreUsable =
		repairedVariants?.length === 2 &&
		normalizedCalibrationIdentity(repairedVariants[0].title) !==
			normalizedCalibrationIdentity(repairedVariants[1].title) &&
		normalizedCalibrationIdentity(repairedVariants[0].body) !==
			normalizedCalibrationIdentity(repairedVariants[1].body) &&
		repairedVariants.every(
			(variant) =>
				normalizedCalibrationIdentity(variant.body) !==
					normalizedCalibrationIdentity(summary.offering || summary.themes) &&
				normalizedCalibrationIdentity(variant.body) !== normalizedCalibrationIdentity(variant.basis)
		);
	const calibrationVariants = hasServerAuthority && variantsAreUsable ? repairedVariants : [];
	return { ...summary, style: repairedStyle, calibrationVariants };
};

export const formatOnboarding1BrandSummary = (summary: Onboarding1BrandSummary) => {
	const variants = summary.calibrationVariants ?? [];
	const audienceFact = hasResolvedOnboarding1BrandFact(summary.audience)
		? ['', '**Кому подходит**', summary.audience]
		: [];
	const optionalFacts = [
		hasResolvedOnboarding1BrandFact(summary.contentGoal)
			? ['', '**Что хотите получить**', summary.contentGoal as string]
			: [],
		hasResolvedOnboarding1BrandFact(summary.publishingCadence)
			? ['', '**Ритм публикаций**', summary.publishingCadence as string]
			: [],
		hasResolvedOnboarding1BrandFact(summary.workflowContext)
			? ['', '**Как будете вести**', summary.workflowContext as string]
			: [],
		hasResolvedOnboarding1BrandFact(summary.customerJob)
			? ['', '**Зачем приходят**', summary.customerJob as string]
			: [],
		hasResolvedOnboarding1BrandFact(summary.trustSignals)
			? ['', '**Почему доверяют**', summary.trustSignals as string]
			: []
	].flat();
	const voiceReview = variants.length
		? [
				'',
				'### Как вам ближе говорить об этом?',
				...variants.flatMap((variant, index) => [
					'',
					`**Вариант ${index + 1} — ${variant.title}**`,
					variant.body,
					`Основа: ${variant.basis}`
				]),
				'',
				'**Какой вариант больше похож на вас?**'
			]
		: ['', 'Сначала подтвердите факты выше — после этого я предложу два варианта подачи.'];
	return [
		`Готово — я изучил ${summary.evidence || 'ваши материалы'}.`,
		'',
		'### Вот что я понял о вашем бизнесе',
		'',
		'**Что предлагаете**',
		summary.offering || summary.themes,
		...audienceFact,
		...optionalFacts,
		...voiceReview,
		'',
		`${ONBOARDING1_BRAND_MEMORY_V2_MARKER} ${encodeURIComponent(JSON.stringify(summary))}`
	].join('\n');
};

/**
 * Render the assistant turn that follows explicit approval of the facts.
 *
 * Facts already live in the preceding review message (and in the encoded
 * summary marker below). Repeating them here makes the conversation look as
 * if the assistant restarted onboarding, so this projection deliberately
 * contains only the Brand Voice choice and the durable summary metadata.
 */
export const formatOnboarding1BrandVoiceContinuation = (summary: Onboarding1BrandSummary) => {
	const variants = summary.calibrationVariants ?? [];
	const voiceReview = variants.length
		? [
				'### Как вам ближе говорить об этом?',
				...variants.flatMap((variant, index) => [
					'',
					`**Вариант ${index + 1} — ${variant.title}**`,
					variant.body,
					`Основа: ${variant.basis}`
				]),
				'',
				'**Какой вариант больше похож на вас?**'
			]
		: ['Факты сохранены. После подготовки вариантов я предложу выбрать подачу.'];
	return [
		'Факты подтверждены. Теперь выберите, как будем говорить о бренде.',
		'',
		...voiceReview,
		'',
		`${ONBOARDING1_BRAND_MEMORY_V2_MARKER} ${encodeURIComponent(JSON.stringify(summary))}`
	].join('\n');
};

const onboarding1BrandSummaryFields = {
	'Голос бренда': 'style',
	'Для кого пишем': 'audience',
	'О чём говорим': 'themes',
	'Как строим посты': 'delivery'
} as const;

export const parseOnboarding1BrandSummary = (content: string): Onboarding1BrandSummary | null => {
	const encodedV2 = content.match(
		new RegExp(`${ONBOARDING1_BRAND_MEMORY_V2_MARKER}\\s+(\\S+)`)
	)?.[1];
	if (encodedV2) {
		try {
			const decoded = JSON.parse(decodeURIComponent(encodedV2)) as Onboarding1BrandSummary;
			if (decoded.style && decoded.audience && decoded.themes && decoded.delivery) {
				return normalizeOnboarding1BrandSummary(decoded);
			}
		} catch {
			// Continue with the readable legacy parser so a partially copied message still reloads.
		}
	}
	if (
		!content.includes('Brand Memory') &&
		!content.includes('Что я понял о вашем проекте') &&
		!content.includes('Вот что я понял о вашем бизнесе')
	)
		return null;

	const values: Partial<Onboarding1BrandSummary> = {};
	let activeField:
		| (typeof onboarding1BrandSummaryFields)[keyof typeof onboarding1BrandSummaryFields]
		| null = null;
	const lines = content.split(/\r?\n/).map((line) => line.trim());

	for (const line of lines) {
		const normalized = line
			.replace(/^[-—]\s*/, '')
			.replace(/^\*\*/, '')
			.replace(/\*\*$/, '')
			.trim();
		const label = Object.keys(onboarding1BrandSummaryFields).find(
			(candidate) => normalized === candidate || normalized.startsWith(`${candidate}:`)
		) as keyof typeof onboarding1BrandSummaryFields | undefined;

		if (label) {
			activeField = onboarding1BrandSummaryFields[label];
			const inlineValue = normalized.slice(label.length).replace(/^:\s*/, '').trim();
			if (inlineValue) values[activeField] = inlineValue;
			continue;
		}

		if (
			activeField &&
			normalized &&
			!/^#{1,6}\s/.test(normalized) &&
			!normalized.startsWith('Готово — я изучил ') &&
			!normalized.startsWith('Вот какую рабочую Brand Memory') &&
			normalized !== 'Всё верно?'
		) {
			values[activeField] = [values[activeField], normalized].filter(Boolean).join(' ');
		}
	}

	const evidenceMatch = content.match(/Готово — я изучил\s+(.+?)\.?(?:\r?\n|$)/);
	if (!values.style || !values.audience || !values.themes || !values.delivery) return null;

	return {
		style: values.style,
		audience: values.audience,
		themes: values.themes,
		delivery: values.delivery,
		evidence: evidenceMatch?.[1]?.replace(/\.$/, '').trim() || 'ваши материалы'
	};
};

export const resolveOnboarding1BrandSummaryEvidence = (
	summary: Onboarding1BrandSummary,
	userMessage: string
): Onboarding1BrandSummary => {
	const evidence = summary.evidence.trim();
	const hasWebsiteLink = /https?:\/\/[^\s)\]}>,]+/i.test(userMessage);
	const evidenceOnlyMentionsDescription = /^(?:ваше\s+)?описание(?:\s+идеи)?$/i.test(evidence);

	if (!hasWebsiteLink || !evidenceOnlyMentionsDescription) return summary;

	return {
		...summary,
		evidence: 'ваш сайт и описание'
	};
};

export const onboarding1MemoryDecision = (
	summary?: Onboarding1BrandSummary
): Onboarding1ComposerDecision => {
	const labels = summary?.calibrationVariants?.slice(0, 2).map((variant) =>
		String(variant.title ?? '')
			.replace(/\s+/g, ' ')
			.trim()
	);

	return {
		id: 'brand-memory-confirmation',
		message: 'Какой вариант больше похож на вас?',
		confirmLabel: labels?.[0] || 'Первый вариант',
		declineLabel: labels?.[1] || 'Второй вариант',
		alternateLabel: 'Оба не подходят'
	};
};

/**
 * Facts are approved separately from Brand Voice. Keeping this as its own
 * decision prevents the voice cards from silently accepting a provisional
 * product or audience inference.
 */
export const onboarding1FactsDecision = (): Onboarding1ComposerDecision => ({
	id: 'brand-facts-confirmation',
	message: 'Всё верно по фактам о вашем бизнесе?',
	confirmLabel: 'Да, всё верно',
	declineLabel: 'Уточнить'
});

export type Onboarding1MemoryReviewInput =
	| 'first'
	| 'second'
	| 'first_with_correction'
	| 'second_with_correction'
	| 'neither'
	| 'explain'
	| 'correction';

export type Onboarding1MemoryReviewChoiceCorrection = Onboarding1ChoiceCorrection;

export const resolveOnboarding1MemoryReviewChoiceCorrection = (
	input: string,
	summary: Onboarding1BrandSummary | null = null
): Onboarding1MemoryReviewChoiceCorrection | null => {
	const calibrationLabels = (summary?.calibrationVariants ?? [])
		.slice(0, 2)
		.map((variant) => String(variant.title ?? ''));
	return resolveOnboarding1ChoiceCorrection({
		input,
		labels: [calibrationLabels[0] ?? '', calibrationLabels[1] ?? '']
	});
};

export const resolveOnboarding1MemoryReviewInput = (
	input: string,
	summary: Onboarding1BrandSummary | null = null
): Onboarding1MemoryReviewInput => {
	const normalized = normalizeOnboarding1ReviewText(input);
	const calibrationLabels = (summary?.calibrationVariants ?? [])
		.slice(0, 2)
		.map((variant) => String(variant.title ?? ''));
	const affirmativeChoice = resolveOnboarding1AffirmativeChoice({
		input,
		labels: [calibrationLabels[0] ?? '', calibrationLabels[1] ?? '']
	});
	if (affirmativeChoice !== null) return affirmativeChoice === 0 ? 'first' : 'second';
	const choiceCorrection = resolveOnboarding1MemoryReviewChoiceCorrection(input, summary);
	if (choiceCorrection) {
		return choiceCorrection.choice === 0 ? 'first_with_correction' : 'second_with_correction';
	}

	if (
		/^(?:первый(?: вариант)?|вариант 1|1|да|верно|вс[её] верно|да вс[её] верно|запомнить|подтверждаю)$/.test(
			normalized
		)
	) {
		return 'first';
	}
	if (/^(?:второй(?: вариант)?|вариант 2|2)$/.test(normalized)) return 'second';
	if (
		/^(?:оба не подходят|ничего не подходит|нет|исправить|неверно|нужно исправить)$/.test(
			normalized
		)
	)
		return 'neither';
	if (/^(?:почему|зачем|что это|что можно|как это)(?:\s|$)/.test(normalized)) return 'explain';
	return 'correction';
};

export const buildOnboarding1BrandCorrectionSource = (
	_summary: Onboarding1BrandSummary | null,
	correction: string
): string => {
	// Previous sources remain attached to the discovery run. Import only the
	// user's factual correction here: product instructions in a source become
	// indistinguishable from business facts for deterministic discovery.
	return correction.trim();
};

type Onboarding1BrandSummaryField =
	| 'style'
	| 'audience'
	| 'themes'
	| 'delivery'
	| 'offering'
	| 'customerJob'
	| 'trustSignals';

const onboarding1CorrectableSummaryFields = [
	'style',
	'audience',
	'themes',
	'delivery',
	'offering',
	'customerJob',
	'trustSignals'
] as const satisfies readonly Onboarding1BrandSummaryField[];

const onboarding1CorrectionFieldMatchers: Record<
	Exclude<Onboarding1BrandSummaryField, 'offering' | 'style' | 'delivery'>,
	RegExp
> = {
	audience: /(?:аудитор|для кого|клиент|покупател|подписчик|читател)/i,
	themes: /(?:тем|о ч[её]м|рубри|направлен|смысл)/i,
	customerJob: /(?:зачем\s+приход|задач|проблем|результат|потребност|сценари)/i,
	trustSignals: /(?:довер|отзыв|гарант|опыт|сертифик|фото|кейс|портфолио|цена)/i
};

const onboarding1CorrectionClauseTargetsField = (
	field: Onboarding1BrandSummaryField,
	clause: string
) =>
	field === 'offering'
		? isOnboarding1OfferingCorrectionClause(clause)
		: field === 'style' || field === 'delivery'
			? isOnboarding1PresentationCorrectionClause(clause, field)
			: onboarding1CorrectionFieldMatchers[field].test(clause);

const correctionExplicitlyPreservesField = (
	field: Onboarding1BrandSummaryField,
	correction: string
) => {
	const clauses = correction
		.split(/[,.!?;\n]+/)
		.map((clause) => clause.trim())
		.filter((clause) => onboarding1CorrectionClauseTargetsField(field, clause));
	return (
		clauses.length > 0 &&
		clauses.every((clause) =>
			/(?:верн|правильн|подходит|остав|не\s+меня|без\s+измен)/i.test(clause)
		)
	);
};

export const mergeOnboarding1BrandCorrectionSummary = ({
	previous,
	next,
	correction
}: {
	previous: Onboarding1BrandSummary | null;
	next: Onboarding1BrandSummary;
	correction: string;
}): Onboarding1BrandSummary => {
	if (!previous) return next;
	const correctionClauses = correction
		.split(/[,.!?;\n]+/)
		.map((clause) => clause.trim())
		.filter(Boolean);
	const targetedFields = onboarding1CorrectableSummaryFields.filter(
		(field) =>
			correctionClauses.some(
				(clause) =>
					onboarding1CorrectionClauseTargetsField(field, clause) &&
					!(
						(field === 'audience' || field === 'offering' || field === 'customerJob') &&
						/(?:не\s+(?:использ|употреб)|избег)\w*[^.!?]*(?:слов|термин|формулиров)/i.test(clause)
					)
			) && !correctionExplicitlyPreservesField(field, correction)
	);
	if (targetedFields.length === 0) return next;
	const targeted = new Set(targetedFields);
	const previousEvidence = (previous.evidenceItems ?? []).join(' ');
	const keepPreviousSemanticFact = (
		field: 'audience' | 'offering' | 'customerJob' | 'trustSignals',
		value: unknown
	) => {
		if (!isCompleteSemanticFact(value)) return false;
		if ((field === 'offering' || field === 'customerJob') && isOwnerIdentityOnly(value))
			return false;
		if ((field === 'offering' || field === 'customerJob') && isMetaCorrectionInstruction(value)) {
			return false;
		}
		if (
			field === 'customerJob' &&
			previous.offering &&
			normalizedCalibrationIdentity(toHumanText(value)) ===
				normalizedCalibrationIdentity(previous.offering)
		) {
			return false;
		}
		if (field === 'audience' && isGenericAudienceLabel(toHumanText(value))) return false;
		if (field === 'trustSignals') {
			return Boolean(previousEvidence) && isStronglySourceGrounded(value, previousEvidence);
		}
		return true;
	};
	const preserveOrUseNext = (
		field: 'audience' | 'offering' | 'customerJob' | 'trustSignals',
		previousValue: string | undefined,
		nextValue: string | undefined
	) => {
		// An explicit user confirmation ("аудиторию оставьте", "факты верны")
		// is stronger evidence than a later model pass over correction text.
		if (
			previousValue &&
			keepPreviousSemanticFact(field, previousValue) &&
			correctionExplicitlyPreservesField(field, correction)
		) {
			return previousValue;
		}
		return targeted.has(field) || !keepPreviousSemanticFact(field, previousValue)
			? nextValue
			: previousValue;
	};
	const merged: Onboarding1BrandSummary = {
		...next,
		style:
			targeted.has('style') || isGenericOnboarding1Style(previous.style)
				? next.style
				: previous.style,
		audience: preserveOrUseNext('audience', previous.audience, next.audience) || next.audience,
		themes: targeted.has('themes') ? next.themes : previous.themes,
		delivery: targeted.has('delivery') ? next.delivery : previous.delivery,
		offering: preserveOrUseNext('offering', previous.offering, next.offering),
		customerJob: preserveOrUseNext('customerJob', previous.customerJob, next.customerJob),
		trustSignals: preserveOrUseNext('trustSignals', previous.trustSignals, next.trustSignals),
		audienceNote:
			targeted.has('audience') || !keepPreviousSemanticFact('audience', previous.audience)
				? next.audienceNote
				: previous.audienceNote
	};
	return {
		...merged,
		calibrationVariants:
			next.calibrationVariants?.length === 2
				? next.calibrationVariants.map((variant) => ({
						...variant,
						basis: displayOnboarding1CalibrationBasis(
							variant.basis,
							merged.offering || merged.themes
						)
					}))
				: buildOnboarding1CalibrationVariants({
						offering: merged.offering || merged.themes,
						audience: merged.audience,
						customerJob: merged.customerJob || '',
						trustSignals: merged.trustSignals || '',
						style: merged.style,
						delivery: merged.delivery,
						themes: merged.themes,
						voiceProfilePayload: {}
					})
	};
};

export const resolveOnboarding1AnalysisRetryStage = (
	flow: Onboarding1FlowState
): Onboarding1FlowStage => {
	if (flow.stage === 'awaiting_memory_correction') return 'awaiting_memory_correction';
	// Once a scratch description has been captured, repeating the brand-seed
	// question loses the user's answer and routes the next turn through the
	// generic scripted copy. Keep the empty cold start on that checkpoint, but
	// resume an answered description at the source/context checkpoint.
	if (
		(flow.stage === 'awaiting_brand_seed' || flow.sourceMode === 'scratch') &&
		!flow.sourceContent.trim()
	) {
		return 'awaiting_brand_seed';
	}
	return 'awaiting_source';
};

export const getOnboarding1FriendlyErrorMessage = (error: unknown): string => {
	if (getOnboardingHttpStatus(error) === 401) {
		return 'сессия обновилась — повторите выбор';
	}
	const raw = onboardingErrorText(error);
	if (/<(?:!doctype|html|body)\b/i.test(raw)) {
		return 'не удалось связаться с сервисом анализа';
	}
	if (/timed?\s*out|timeout/i.test(raw)) return 'анализ занял больше времени, чем обычно';
	if (/load\s+failed|failed\s+to\s+fetch|network|connection|fetch/i.test(raw)) {
		return 'не удалось связаться с сервисом анализа';
	}
	if (/model\s+not\s+found|model[^\n]*(?:unavailable|not\s+available)/i.test(raw)) {
		return 'сервис подготовки результата сейчас недоступен';
	}
	if (
		/service_unavailable|service\s+temporarily\s+unavailable|upstream_provider_error/i.test(raw)
	) {
		return 'сервис подготовки результата временно недоступен';
	}
	if (/brand\s+voice|deep\s+llm|provider/i.test(raw)) {
		return 'не удалось завершить анализ стиля';
	}
	return raw.trim() || 'не удалось завершить шаг';
};

export const formatOnboarding1ResultFailureMessage = ({
	message,
	firstPost = false
}: {
	message: string;
	firstPost?: boolean;
}): string => {
	const normalized = String(message ?? '')
		.trim()
		.replace(
			/\s*Повторите шаг\s*[—-]\s*описание проекта и выбранная подача сохранены[.!?…]*$/iu,
			''
		)
		.replace(/[.!?…]+$/u, '');
	const sentence = normalized
		? `${normalized.charAt(0).toLocaleUpperCase('ru-RU')}${normalized.slice(1)}`
		: 'Не удалось завершить шаг';
	const result = firstPost
		? 'Первый пост пока не создался'
		: 'Следующий результат пока не подготовился';

	return `${result}. ${sentence}.\n\nОписание проекта и выбранная подача сохранены — можно повторить шаг.`;
};

export const isOnboarding1FirstResultRetryRequest = (value: string): boolean =>
	/(?:повтор(?:ить|но)|снова|заново|ещ[её]\s+раз)/iu.test(value.trim()) &&
	/(?:пост|генерац|результат|шаг|подготов)/iu.test(value.trim());

export const canRetryOnboarding1FirstResult = (
	flow: Onboarding1FlowState,
	prompt: string
): boolean =>
	['awaiting_brand_voice', 'awaiting_task', 'memory_review'].includes(flow.stage) &&
	flow.brandReady &&
	Boolean(flow.selectedCalibrationChoiceRef.trim() && flow.topic.trim()) &&
	isOnboarding1FirstResultRetryRequest(prompt);

export const shouldEnrichOnboarding1BrandVoice = ({
	sourceMode,
	links = []
}: {
	sourceMode: Onboarding1FlowState['sourceMode'];
	links?: string[];
}): boolean => {
	// A link is an existing brand source even when a restored/legacy flow has
	// lost its sourceMode. Without this precedence the link path falls through
	// to `memory_review`, renders calibration variants in the same response as
	// the facts, and skips the explicit facts confirmation checkpoint.
	// Existing content lets the model analyse an established voice; a scratch
	// brief lets it propose two grounded directions. In both cases the samples
	// must be generated from this brand instead of replaced with method copy.
	return links.length > 0 || sourceMode === 'existing' || sourceMode === 'scratch';
};

export const resolveOnboarding1AnalysisSourceIds = ({
	allSourceIds,
	linkSourceIds,
	isCorrection
}: {
	allSourceIds: string[];
	linkSourceIds: string[];
	isCorrection: boolean;
}): string[] => {
	const all = unique(allSourceIds.map(String).filter(Boolean));
	const linked = unique(linkSourceIds.map(String).filter(Boolean));
	// A page and the user's own explanation complement one another: the page
	// grounds public facts, while the message often contains the actual offer,
	// audience and first task that are absent from a sparse site. Discovery
	// reconciles both sources instead of silently dropping either one.
	return unique([...linked, ...all]);
};

export const analyzeOnboarding1BrandSource = async ({
	token,
	flow,
	sourceText,
	intentId,
	conversationId,
	startFresh = false
}: {
	token: string;
	flow: Onboarding1FlowState;
	sourceText: string;
	intentId: string;
	conversationId: string;
	startFresh?: boolean;
}): Promise<Onboarding1FlowState> => {
	// Snapshot reactive state before the first await. During a long provider call
	// Svelte can replace/mutate the live flow object; a correction must always be
	// merged into the exact factual summary the user was reviewing when they sent it.
	const previousSummary = flow.summary ? structuredClone(flow.summary) : null;
	const state = await ensureOnboardingState(token, flow, startFresh);
	const stateId = String(state.state_id ?? '').trim();
	if (!stateId) throw new Error('Не удалось создать состояние онбординга');
	const isCorrection = flow.stage === 'awaiting_memory_correction';
	const correctionTarget = isCorrection
		? flow.correctionTarget || (flow.brandVoiceRunId ? 'brand_voice' : 'facts')
		: '';
	const isBrandVoiceCorrection = isCorrection && correctionTarget === 'brand_voice';
	const referenceScope = isCorrection
		? flow.referenceScope
		: resolveOnboarding1ReferenceScope(sourceText);
	const sourceRole = isCorrection ? '' : resolveOnboarding1SourceRole(sourceText);
	const analysisText = isCorrection
		? buildOnboarding1BrandCorrectionSource(flow.summary, sourceText)
		: sourceText.trim();
	const links = extractOnboarding1Links(sourceText);
	const descriptionText = links.length > 0 ? removeOnboarding1Links(sourceText) : analysisText;
	const importedResults: Record<string, any>[] = [];
	let linkImportResult: Record<string, any> | null = null;
	let textImportResult: Record<string, any> | null = null;
	if (links.length > 0) {
		linkImportResult = (await addOnboardingLinkSources(token, {
			state_id: stateId,
			links,
			client_request_id: makeIdempotencyKey('onboarding1-links-client', stateId),
			idempotency_key: makeIdempotencyKey('onboarding1-links', stateId),
			metadata_json: {
				onboarding1_intent: intentId,
				onboarding1_reference_scope: referenceScope || undefined,
				onboarding_source_role: sourceRole || undefined,
				onboarding1_source_kind: isCorrection
					? 'brand_memory_correction_reference'
					: sourceRole || (referenceScope ? 'brand_style_reference' : 'brand_source')
			}
		})) as Record<string, any>;
		assertOnboarding1LinkImportSucceeded(linkImportResult);
		importedResults.push(linkImportResult);
	}
	if (links.length === 0 || isCorrection || descriptionText.length >= 20) {
		textImportResult = (await addOnboardingTextSource(token, {
			state_id: stateId,
			title: isCorrection
				? 'Уточнение пользователя'
				: flow.stage === 'awaiting_brand_seed'
					? 'Основа нового бренда'
					: 'Материалы бренда',
			text: isCorrection ? analysisText : descriptionText,
			idempotency_key: makeIdempotencyKey('onboarding1-text', stateId),
			metadata_json: {
				onboarding1_intent: intentId,
				onboarding1_reference_scope: referenceScope || undefined,
				onboarding_source_role: sourceRole || undefined,
				onboarding1_source_kind: isCorrection
					? 'brand_memory_correction'
					: flow.sourceMode === 'scratch'
						? 'brand_brief'
						: links.length > 0
							? sourceRole || 'brand_source_description'
							: 'brand_source'
			}
		})) as Record<string, any>;
		importedResults.push(textImportResult);
	}
	const refreshedState = await getOnboardingState(token, stateId).catch(() => null);
	const currentState = refreshedState ?? state;
	const onboardingRevision = normalizeOnboardingRevision(refreshedState?.revision);
	const brandId = String(
		importedResults.map((item) => importIdentity(item, 'brand_id')).find(Boolean) ||
			String(currentState.brand_id ?? '').trim()
	).trim();
	const workspaceId = String(
		importedResults.map((item) => importIdentity(item, 'workspace_id')).find(Boolean) ||
			String(currentState.workspace_id ?? '').trim()
	).trim();
	if (!brandId || !workspaceId) {
		throw new Error('Не удалось определить бренд после импорта материалов');
	}
	const sourceIds = unique([
		...flow.sourceIds,
		...importedResults.flatMap((item) =>
			((item.source_ids ?? item.source_ids_json ?? []) as unknown[]).map(String)
		)
	]);
	const priorAnalysisSourceIds = unique(
		flow.analysisSourceIds.length ? flow.analysisSourceIds : flow.sourceIds
	);
	const textSourceIds = textImportResult
		? ((textImportResult.source_ids ?? textImportResult.source_ids_json ?? []) as unknown[]).map(
				String
			)
		: [];
	const linkSourceIds = linkImportResult
		? ((linkImportResult.source_ids ?? linkImportResult.source_ids_json ?? []) as unknown[]).map(
				String
			)
		: [];
	const discoverySourceIds = referenceScope
		? unique([...flow.sourceIds, ...textSourceIds])
		: resolveOnboarding1AnalysisSourceIds({
				allSourceIds: sourceIds,
				linkSourceIds,
				isCorrection
			});
	const brandVoiceSourceIds = isCorrection ? priorAnalysisSourceIds : discoverySourceIds;
	const brandVoiceGuidance = isCorrection
		? unique([...flow.brandVoiceGuidance, sourceText.trim()])
		: flow.brandVoiceGuidance;
	const importSessionId = String(
		[...importedResults]
			.reverse()
			.map((item) => item.session_id)
			.find(Boolean) ??
			currentState.active_import_session_id ??
			''
	).trim();
	const discoveryRun = (await startOnboardingBrandDiscovery(token, {
		state_id: stateId,
		conversation_id: conversationId,
		source_ids: discoverySourceIds,
		idempotency_key: makeIdempotencyKey('onboarding1-discovery', stateId)
	})) as BrandDiscoveryRun;
	const loadPendingDiscoveryQuestion = async () => {
		const [currentRun, questionResult] = await Promise.all([
			getOnboardingBrandDiscovery(token, discoveryRun.run_id),
			getOnboardingBrandDiscoveryQuestions(token, discoveryRun.run_id)
		]);
		return projectScopedOnboardingPendingQuestion({
			scope: {
				stateId,
				workspaceId,
				brandId,
				discoveryRunId: discoveryRun.run_id
			},
			run: currentRun,
			questions: questionResult.items ?? []
		});
	};
	const projectPendingQuestionFlow = (
		pendingDiscoveryQuestion: OnboardingDiscoveryQuestionContinuationV1,
		freshSummary: Onboarding1BrandSummary | null = previousSummary
	) => {
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(onboarding1StateStorageKey, stateId);
		}
		return createOnboarding1FlowState({
			...flow,
			stage: 'awaiting_discovery_question',
			intentId,
			stateId,
			workspaceId,
			brandId,
			onboardingRevision,
			importSessionId,
			sourceIds,
			analysisSourceIds: brandVoiceSourceIds,
			brandVoiceGuidance,
			discoveryRunId: pendingDiscoveryQuestion.runRef,
			brandVoiceRunId: '',
			brandVoiceReviewMessageId: '',
			correctionTarget,
			referenceScope,
			pendingDiscoveryQuestion,
			// The question is a checkpoint inside the same discovery run. Keep the
			// latest server projection beside it so history never re-renders the
			// previous import title or a stale one-field card while the user answers.
			summary: freshSummary ?? undefined
		});
	};
	const initialPendingDiscoveryQuestion = await loadPendingDiscoveryQuestion();
	if (initialPendingDiscoveryQuestion) {
		const [factsRun, factsResult] = await Promise.all([
			getOnboardingBrandDiscovery(token, discoveryRun.run_id),
			getOnboardingBrandDiscoveryFindings(token, discoveryRun.run_id)
		]);
		const factsSummary = buildOnboarding1BrandSummary(
			factsRun,
			factsResult.items ?? [],
			[],
			sourceText,
			null
		);
		return projectPendingQuestionFlow(initialPendingDiscoveryQuestion, factsSummary);
	}
	if (shouldEnrichOnboarding1BrandVoice({ sourceMode: flow.sourceMode, links })) {
		const [factsRun, factsResult] = await Promise.all([
			getOnboardingBrandDiscovery(token, discoveryRun.run_id),
			getOnboardingBrandDiscoveryFindings(token, discoveryRun.run_id)
		]);
		const factsSummary = buildOnboarding1BrandSummary(
			factsRun,
			factsResult.items ?? [],
			[],
			sourceText,
			null
		);
		if (typeof localStorage !== 'undefined') {
			localStorage.setItem(onboarding1StateStorageKey, stateId);
		}
		return createOnboarding1FlowState({
			...flow,
			stage: isBrandVoiceCorrection ? 'awaiting_brand_voice' : 'awaiting_fact_confirmation',
			intentId,
			stateId,
			workspaceId,
			brandId,
			importSessionId,
			sourceIds,
			analysisSourceIds: brandVoiceSourceIds,
			brandVoiceGuidance,
			discoveryRunId: discoveryRun.run_id,
			brandVoiceRunId: '',
			brandVoiceReviewMessageId: '',
			factsReviewMessageId: '',
			correctionTarget,
			referenceScope,
			pendingDiscoveryQuestion: null,
			correctionCount:
				flow.stage === 'awaiting_memory_correction'
					? flow.correctionCount + 1
					: flow.correctionCount,
			onboardingRevision,
			summary: isBrandVoiceCorrection
				? previousSummary
				: isCorrection
					? mergeOnboarding1BrandCorrectionSummary({
							previous: previousSummary,
							next: factsSummary,
							correction: sourceText
						})
					: factsSummary
		});
	}
	const brandVoiceRunId = '';
	const [run, discoveryFindingsResult, voiceFindingsResult, calibrationAuthority] =
		await Promise.all([
			getOnboardingBrandDiscovery(token, discoveryRun.run_id),
			getOnboardingBrandDiscoveryFindings(token, discoveryRun.run_id),
			brandVoiceRunId
				? listBrandVoiceFindings(token, brandId, brandVoiceRunId)
				: Promise.resolve({ items: [] }),
			brandVoiceRunId
				? getOnboardingBrandVoiceCalibration(token, discoveryRun.run_id, brandVoiceRunId)
				: Promise.resolve(null)
		]);
	const discoveredSummary = buildOnboarding1BrandSummary(
		run,
		discoveryFindingsResult.items ?? [],
		(voiceFindingsResult?.items ?? []) as BrandVoiceFinding[],
		sourceText,
		calibrationAuthority
	);
	const summary = isCorrection
		? mergeOnboarding1BrandCorrectionSummary({
				previous: previousSummary,
				next: discoveredSummary,
				correction: sourceText
			})
		: discoveredSummary;
	if (typeof localStorage !== 'undefined') {
		localStorage.setItem(onboarding1StateStorageKey, stateId);
	}
	return createOnboarding1FlowState({
		...flow,
		stage: 'memory_review',
		intentId,
		stateId,
		workspaceId,
		brandId,
		onboardingRevision,
		importSessionId,
		sourceIds,
		analysisSourceIds: isCorrection ? priorAnalysisSourceIds : brandVoiceSourceIds,
		brandVoiceGuidance,
		discoveryRunId: run.run_id,
		brandVoiceRunId,
		pendingDiscoveryQuestion: null,
		referenceScope,
		correctionCount:
			flow.stage === 'awaiting_memory_correction' ? flow.correctionCount + 1 : flow.correctionCount,
		summary
	});
};

export class Onboarding1DiscoveryAnswerError extends Error {
	readonly code:
		| 'scope_mismatch'
		| 'question_stale'
		| 'answer_invalid'
		| 'response_mismatch'
		| 'ownership_lost'
		| 'summary_incomplete';

	constructor(code: Onboarding1DiscoveryAnswerError['code'], message: string) {
		super(message);
		this.name = 'Onboarding1DiscoveryAnswerError';
		this.code = code;
	}
}

export type Onboarding1DiscoveryAnswerOperations = Readonly<{
	loadRun: (
		token: string,
		runRef: string,
		options?: { signal?: AbortSignal }
	) => Promise<BrandDiscoveryRun>;
	loadQuestions: (
		token: string,
		runRef: string,
		options?: { signal?: AbortSignal }
	) => Promise<{ items: BrandDiscoveryQuestion[] }>;
	loadFindings: (token: string, runRef: string) => Promise<{ items: BrandDiscoveryFinding[] }>;
	persistAnswer: typeof answerOnboardingBrandDiscoveryQuestion;
	digest: (value: string) => Promise<string>;
	generateCalibration?: (
		token: string,
		flow: Onboarding1FlowState,
		questions: BrandDiscoveryQuestion[],
		conversationId: string,
		options?: {
			signal?: AbortSignal;
			isCurrent?: () => boolean;
			retry?: {
				operation_ref: string;
				expected_attempt: number;
				recoveryAction?: 'adopt_existing' | 'regenerate_unknown';
			};
			onBrandVoiceGenerationStarted?: () => void | Promise<void>;
		}
	) => Promise<{
		voiceRunId: string;
		voiceFindings: BrandVoiceFinding[];
		authority: BrandVoiceCalibrationAuthorityV1;
	}>;
}>;

const sha256Hex = async (value: string): Promise<string> => {
	const subtle = globalThis.crypto?.subtle;
	if (!subtle) {
		throw new Onboarding1DiscoveryAnswerError(
			'scope_mismatch',
			'Невозможно безопасно связать ответ с текущим вопросом'
		);
	}
	const bytes = await subtle.digest('SHA-256', new TextEncoder().encode(value));
	return [...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
};

const defaultOnboarding1DiscoveryAnswerOperations: Onboarding1DiscoveryAnswerOperations = {
	loadRun: getOnboardingBrandDiscovery,
	loadQuestions: getOnboardingBrandDiscoveryQuestions,
	loadFindings: getOnboardingBrandDiscoveryFindings,
	persistAnswer: answerOnboardingBrandDiscoveryQuestion,
	digest: sha256Hex,
	generateCalibration: async (token, flow, _questions, conversationId, options) => {
		let current = assertOnboardingCalibrationContinuation(
			await getOnboardingCalibrationContinuation(
				token,
				flow.discoveryRunId,
				conversationId,
				options
			),
			flow,
			conversationId
		);
		if (options?.signal?.aborted || options?.isCurrent?.() === false)
			throw new Onboarding1DiscoveryAnswerError('ownership_lost', 'Onboarding view changed');
		const retry = options?.retry;
		if (retry && retry.operation_ref !== current.operation_ref)
			throw new Onboarding1DiscoveryAnswerError(
				'scope_mismatch',
				'Onboarding continuation changed'
			);
		current = await executeUnknownCalibrationRecovery({
			token,
			flow,
			conversationId,
			current,
			retry,
			signal: options?.signal,
			isCurrent: options?.isCurrent,
			onGenerationStarted: options?.onBrandVoiceGenerationStarted
		});
		if (!retry?.recoveryAction && (current.status === 'available' || current.status === 'failed')) {
			await options?.onBrandVoiceGenerationStarted?.();
			if (options?.signal?.aborted || options?.isCurrent?.() === false)
				throw new Onboarding1DiscoveryAnswerError('ownership_lost', 'Onboarding view changed');
			current = assertOnboardingCalibrationContinuation(
				await continueOnboardingCalibration(
					token,
					flow.discoveryRunId,
					{
						conversation_id: conversationId,
						operation_ref: current.operation_ref,
						expected_attempt: current.attempt
					},
					options
				),
				flow,
				conversationId
			);
		}
		if (options?.signal?.aborted || options?.isCurrent?.() === false)
			throw new Onboarding1DiscoveryAnswerError('ownership_lost', 'Onboarding view changed');
		if (current.status !== 'completed' || !current.authority || !current.voice_run_ref)
			throw new OnboardingCalibrationPendingError(current);
		const voiceRunId = current.voice_run_ref;
		const authority = current.authority;
		const voiceFindings = await listBrandVoiceFindings(token, flow.brandId, voiceRunId);
		return {
			voiceRunId,
			voiceFindings: (voiceFindings?.items ?? []) as BrandVoiceFinding[],
			authority
		};
	}
};

const sameStringArray = (left: unknown, right: string[]): boolean =>
	Array.isArray(left) &&
	left.length === right.length &&
	left.every((value, index) => value === right[index]);

export const answerOnboarding1PendingDiscoveryQuestion = async ({
	token,
	conversationId,
	flow,
	answer,
	operations = defaultOnboarding1DiscoveryAnswerOperations,
	onAnswerAccepted,
	onBrandVoiceGenerationStarted,
	signal,
	isCurrent
}: {
	token: string;
	conversationId: string;
	flow: Onboarding1FlowState;
	answer: string;
	operations?: Onboarding1DiscoveryAnswerOperations;
	onAnswerAccepted?: () => void | Promise<void>;
	onBrandVoiceGenerationStarted?: () => void | Promise<void>;
	signal?: AbortSignal;
	isCurrent?: () => boolean;
}): Promise<Onboarding1FlowState> => {
	const assertCurrent = () => {
		if (signal?.aborted || isCurrent?.() === false) {
			throw new Onboarding1DiscoveryAnswerError(
				'ownership_lost',
				'Этот ответ больше не принадлежит текущему шагу'
			);
		}
	};
	assertCurrent();
	const requestOptions: [options?: { signal?: AbortSignal }] = signal ? [{ signal }] : [];
	const runRef = flow.discoveryRunId.trim();
	const conversationRef = conversationId.trim();
	if (
		flow.stage !== 'awaiting_discovery_question' ||
		!runRef ||
		!conversationRef ||
		!flow.stateId ||
		!flow.workspaceId ||
		!flow.brandId
	) {
		throw new Onboarding1DiscoveryAnswerError(
			'scope_mismatch',
			'Текущий вопрос больше не принадлежит этому онбордингу'
		);
	}

	const [run, questionResult] = await Promise.all([
		operations.loadRun(token, runRef, ...requestOptions),
		operations.loadQuestions(token, runRef, ...requestOptions)
	]);
	assertCurrent();
	const continuation = projectOnboardingDiscoveryQuestionContinuation({
		run,
		questions: questionResult.items ?? []
	});
	if (
		!continuation ||
		continuation.runRef !== runRef ||
		continuation.workspaceRef !== flow.workspaceId ||
		continuation.brandRef !== flow.brandId ||
		run.state_id !== flow.stateId ||
		(flow.pendingDiscoveryQuestion !== null &&
			!sameOnboardingDiscoveryQuestion(continuation, flow.pendingDiscoveryQuestion))
	) {
		throw new Onboarding1DiscoveryAnswerError(
			'question_stale',
			'Уточняющий вопрос уже изменился. Обновите экран и продолжите с актуального шага.'
		);
	}

	const normalizedAnswer = answer.trim();
	const option = continuation.options.find(
		(candidate) => candidate.label === normalizedAnswer || candidate.value === normalizedAnswer
	);
	if (!normalizedAnswer || (!option && !continuation.allowCustomAnswer)) {
		throw new Onboarding1DiscoveryAnswerError(
			'answer_invalid',
			'Ответ не соответствует текущему серверному вопросу'
		);
	}
	const selectedValues = option ? [option.value] : [];
	const customAnswer = option ? '' : normalizedAnswer;
	const answerDigest = await operations.digest(
		JSON.stringify([conversationRef, continuation.questionRef, selectedValues, customAnswer])
	);
	assertCurrent();
	const idempotencyKey = `onboarding1-discovery-answer-${continuation.questionRef}-${answerDigest.slice(0, 24)}`;
	const updated = await operations.persistAnswer(
		token,
		continuation.runRef,
		continuation.questionRef,
		{
			conversation_id: conversationRef,
			selected_values: selectedValues,
			custom_answer: customAnswer || undefined,
			normalized_answer_json: {
				selected_values: selectedValues,
				...(customAnswer && { custom_answer: customAnswer })
			},
			idempotency_key: idempotencyKey
		},
		...requestOptions
	);
	assertCurrent();
	if (
		updated.id !== continuation.questionRef ||
		updated.run_id !== continuation.runRef ||
		updated.workspace_id !== continuation.workspaceRef ||
		updated.brand_id !== continuation.brandRef ||
		updated.status !== 'answered' ||
		!sameStringArray(updated.answer_json?.selected_values, selectedValues) ||
		String(updated.answer_json?.custom_answer ?? '') !== customAnswer
	) {
		throw new Onboarding1DiscoveryAnswerError(
			'response_mismatch',
			'Сервер не подтвердил точную привязку ответа к вопросу'
		);
	}

	try {
		// Checkpoint the separate answer pair only after the server confirmed it;
		// downstream reads/calibration may otherwise lose the current owner.
		await onAnswerAccepted?.();
		assertCurrent();
		const [currentRun, currentQuestions] = await Promise.all([
			operations.loadRun(token, runRef, ...requestOptions),
			operations.loadQuestions(token, runRef, ...requestOptions)
		]);
		assertCurrent();
		const nextQuestion = projectOnboardingDiscoveryQuestionContinuation({
			run: currentRun,
			questions: currentQuestions.items ?? []
		});
		if (nextQuestion) {
			const refreshedFindings = await operations.loadFindings(token, runRef);
			const factsSummary = buildOnboarding1BrandSummary(
				currentRun,
				refreshedFindings.items ?? [],
				[],
				flow.sourceContent,
				null
			);
			return createOnboarding1FlowState({
				...flow,
				stage: 'awaiting_discovery_question',
				brandVoiceReviewMessageId: '',
				pendingDiscoveryQuestion: nextQuestion,
				summary: factsSummary
			});
		}
		if (flow.correctionTarget === 'brand_voice') {
			return createOnboarding1FlowState({
				...flow,
				stage: 'awaiting_brand_voice',
				brandVoiceRunId: '',
				pendingDiscoveryQuestion: null
			});
		}

		const factsSummary = buildOnboarding1BrandSummary(
			currentRun,
			(await operations.loadFindings(token, runRef)).items ?? [],
			[],
			flow.sourceContent,
			null
		);
		return createOnboarding1FlowState({
			...flow,
			stage: 'awaiting_fact_confirmation',
			brandVoiceRunId: '',
			factsReviewMessageId: '',
			pendingDiscoveryQuestion: null,
			summary: factsSummary
		});
	} catch (error) {
		throw new OnboardingAcceptedAnswerContinuationError(error);
	}
};

/** Only the already accepted server answers feed this stage; never another answer POST. */
export const continueOnboarding1Calibration = async ({
	token,
	conversationId,
	flow,
	operations = defaultOnboarding1DiscoveryAnswerOperations,
	signal,
	isCurrent,
	onBrandVoiceGenerationStarted,
	retry
}: {
	token: string;
	conversationId: string;
	flow: Onboarding1FlowState;
	operations?: Onboarding1DiscoveryAnswerOperations;
	signal?: AbortSignal;
	isCurrent?: () => boolean;
	onBrandVoiceGenerationStarted?: () => void | Promise<void>;
	retry?: {
		operation_ref: string;
		expected_attempt: number;
		recoveryAction?: 'adopt_existing' | 'regenerate_unknown';
	};
}): Promise<Onboarding1FlowState> => {
	const assertCurrent = () => {
		if (signal?.aborted || isCurrent?.() === false)
			throw new Onboarding1DiscoveryAnswerError('ownership_lost', 'Onboarding view changed');
	};
	assertCurrent();
	if (flow.stage !== 'awaiting_brand_voice' || !conversationId.trim())
		throw new Onboarding1DiscoveryAnswerError('scope_mismatch', 'Onboarding continuation changed');
	const runRef = flow.discoveryRunId;
	const [currentRun, currentQuestions] = await Promise.all([
		operations.loadRun(token, runRef, { signal }),
		operations.loadQuestions(token, runRef, { signal })
	]);
	assertCurrent();
	if (
		projectScopedOnboardingPendingQuestion({
			scope: flow,
			run: currentRun,
			questions: currentQuestions.items
		}) ||
		currentQuestions.items.some((q) => q.status !== 'answered')
	)
		throw new Onboarding1DiscoveryAnswerError(
			'question_stale',
			'Onboarding answers are not settled'
		);
	if (!operations.generateCalibration) {
		throw new Onboarding1DiscoveryAnswerError(
			'summary_incomplete',
			'Сервер не выдал авторитетные варианты Brand Voice'
		);
	}
	const [findingResult, generated] = await Promise.all([
		operations.loadFindings(token, runRef),
		operations.generateCalibration(token, flow, currentQuestions.items, conversationId, {
			signal,
			isCurrent,
			retry,
			onBrandVoiceGenerationStarted
		})
	]);
	assertCurrent();
	const summary = buildOnboarding1BrandSummary(
		currentRun,
		findingResult.items ?? [],
		generated.voiceFindings,
		flow.sourceContent,
		generated.authority
	);
	if (
		summary.calibrationVariants?.length !== 2 ||
		summary.calibrationVariants[0].id === summary.calibrationVariants[1].id ||
		summary.calibrationVariants[0].body === summary.calibrationVariants[1].body
	) {
		throw new Onboarding1DiscoveryAnswerError(
			'summary_incomplete',
			'После уточнения не удалось подготовить два самостоятельных варианта Brand Voice'
		);
	}
	return createOnboarding1FlowState({
		...flow,
		stage: 'memory_review',
		brandVoiceRunId: generated.voiceRunId,
		pendingDiscoveryQuestion: null,
		correctionCount: flow.correctionCount,
		summary
	});
};

const onboarding1GuidedResultSignals: Record<string, RegExp> = {
	create_content_plan: /(?:контент[- ]план|публикац|рубри|недел|день)/i,
	suggest_topics: /(?:иде|тем|угол|заход|пост)/i,
	fill_content_calendar: /(?:календар|дат|день|недел|публикац)/i,
	plan_content_campaign: /(?:кампани|этап|цель|метрик|публикац)/i,
	create_short_video_scripts: /(?:видео|ролик|reels|shorts|сценар|кадр)/i,
	suggest_short_video_ideas: /(?:видео|ролик|reels|shorts|иде|сценар)/i,
	turn_posts_into_short_videos: /(?:пост|видео|ролик|reels|shorts|сценар)/i,
	create_short_video_campaign: /(?:видео|кампани|ролик|этап|сценар)/i,
	suggest_content_strategy: /(?:стратег|цель|аудитор|канал|приоритет)/i,
	build_content_system: /(?:систем|рубри|ритм|процесс|регуляр)/i,
	create_content_starter_kit: /(?:старт|план|пост|сценар|шаг)/i,
	help_choose_first_step: /(?:перв|шаг|начать|приоритет|рекоменд)/i
};

export const isOnboarding1GuidedResultCompletionCandidate = ({
	flow,
	content,
	hasError = false
}: {
	flow: Onboarding1FlowState;
	content: string;
	hasError?: boolean;
}): boolean => {
	const text = content.trim();
	const listItems = text.match(/^\s*(?:[-*•]|\d+[.)])\s+/gm)?.length ?? 0;
	const tableRows = text.match(/^\s*\|.*\|\s*$/gm)?.length ?? 0;
	const headings = text.match(/^#{1,4}\s+\S+/gm)?.length ?? 0;
	const campaignStages = text.match(/^\s*\*{0,2}[1-3][.)]\s+.+?\*{0,2}\s*$/gm)?.length ?? 0;
	const hasUsefulStructure =
		listItems >= 2 ||
		tableRows >= 3 ||
		headings >= 2 ||
		(flow.intentId === 'plan_content_campaign' &&
			campaignStages >= 3 &&
			/что\s+получится/i.test(text));
	const intentSignal = onboarding1GuidedResultSignals[flow.intentId];
	const hasClosingSignal =
		text.length >= 400 &&
		/(?:если хотите|следующ(?:им|ий)|готов(?:о|ы)?|можно|начн(?:ём|ем)|перв(?:ый|ые)|дальше)/i.test(
			text.slice(-260)
		);
	const canFinalizeFlow =
		Boolean(flow.stateId) || (flow.brandReady && Boolean(flow.brandId.trim()));
	const hasPendingBrandVoiceReview =
		Boolean(flow.brandVoiceRunId.trim()) && !flow.selectedCalibrationChoiceRef.trim();
	const starterRequiresReadyBrand =
		flow.intentId === 'create_content_starter_kit' && !flow.brandReady;

	return (
		flow.stage === 'generating_result' &&
		!hasPendingBrandVoiceReview &&
		!isOnboarding1PostIntent(flow.intentId) &&
		!starterRequiresReadyBrand &&
		canFinalizeFlow &&
		text.length >= 220 &&
		(hasUsefulStructure || (hasError && hasClosingSignal)) &&
		Boolean(intentSignal?.test(text)) &&
		(!hasError || hasClosingSignal)
	);
};

export const completeOnboarding1GuidedResult = async (
	token: string,
	flow: Onboarding1FlowState
): Promise<Onboarding1FlowState> => {
	if (flow.brandVoiceRunId.trim() && !flow.selectedCalibrationChoiceRef.trim()) {
		throw new Error('Сначала выберите вариант Brand Voice');
	}
	if (flow.intentId === 'create_content_starter_kit' && !flow.brandReady) {
		throw new Error('Сначала подтвердите основу бренда');
	}
	if (flow.stateId) {
		await completeOnboarding(token, {
			state_id: flow.stateId,
			idempotency_key: makeIdempotencyKey('onboarding1-guided-complete', flow.stateId)
		});
	} else if (!flow.brandReady || !flow.brandId.trim()) {
		throw new Error('Не найдено состояние онбординга');
	}
	return createOnboarding1FlowState({ ...flow, stage: 'completed' });
};

const BRAND_VOICE_SAFE_CATEGORIES = new Set([
	'voice_profile',
	'audience_profile',
	'platform_rule',
	'topic_cluster',
	'post_type_template',
	'validation_rubric',
	'golden_example',
	'writing_rule',
	'do_dont'
]);

const SCOPED_REFERENCE_SAFE_CATEGORIES = new Set([
	'voice_profile',
	'platform_rule',
	'post_type_template',
	'validation_rubric',
	'writing_rule',
	'do_dont'
]);

const DEFAULT_ONBOARDING1_MEMORY_CONFIRM_TIMEOUT_MS = 30_000;

type Onboarding1MemoryConfirmOptions = {
	timeoutMs?: number;
	maxAttempts?: number;
	retryDelayMs?: number;
	/** Internal observability seam used by deterministic preview and regression tests. */
	onAttempt?: (event: {
		attempt: number;
		status: 'started' | 'retrying' | 'succeeded' | 'failed';
		error?: unknown;
	}) => void | Promise<void>;
	/** Internal operation seam; production callers use the real idempotent transaction. */
	operation?: (
		auth: OnboardingLiveAuthSource,
		flow: Onboarding1FlowState
	) => Promise<Onboarding1FlowState>;
};

const isNonRetryableOnboarding1MemoryConfirmError = (error: unknown) =>
	isOnboardingUnauthorizedError(error) ||
	(error instanceof Error && error.message === 'Не найден готовый анализ бренда');

const delayOnboarding1MemoryConfirmRetry = (delayMs: number) =>
	delayMs > 0 ? new Promise<void>((resolve) => setTimeout(resolve, delayMs)) : Promise.resolve();

const confirmOnboarding1BrandMemoryOperation = async (
	auth: OnboardingLiveAuthSource,
	flow: Onboarding1FlowState
): Promise<Onboarding1FlowState> => {
	if (!flow.discoveryRunId || !flow.brandId) {
		throw new Error('Не найден готовый анализ бренда');
	}
	// Discovery is the durable Brand Memory transaction. Voice analysis is an
	// optional enrichment layer and must never make an already valid brand base
	// look unsaved to the user.
	const selectedCalibration = flow.summary?.calibrationVariants?.find(
		(variant) => variant.id === flow.selectedCalibrationId
	);
	const choiceSetRef = flow.summary?.calibrationChoiceSetRef?.trim() ?? '';
	const choiceRef = selectedCalibration?.choiceRef?.trim() ?? '';
	if (!selectedCalibration || !flow.brandVoiceRunId || !choiceSetRef || !choiceRef) {
		throw new Error('Серверный вариант Brand Voice больше не доступен для выбора');
	}
	const getToken = toOnboardingLiveAuthSupplier(auth);
	const runStep = <Result>(request: (token: string) => Promise<Result>) =>
		runOnboardingLiveAuthStep(getToken, request);
	const discoveryResult = await runStep((token) =>
		getOnboardingBrandDiscoveryFindings(token, flow.discoveryRunId)
	);
	if ((discoveryResult.items ?? []).some((finding) => finding.status === 'pending')) {
		throw new Error('Сначала подтвердите факты о бизнесе');
	}
	const appliedDiscovery = await runStep((token) =>
		applyOnboardingBrandDiscovery(token, flow.discoveryRunId, {
			idempotency_key: makeIdempotencyKey('onboarding1-discovery-apply', flow.discoveryRunId),
			voice_run_ref: flow.brandVoiceRunId,
			choice_set_ref: choiceSetRef,
			choice_ref: choiceRef
		})
	);
	if (appliedDiscovery.status === 'needs_review') {
		if (
			appliedDiscovery.run_id !== flow.discoveryRunId ||
			appliedDiscovery.brand_id !== flow.brandId ||
			appliedDiscovery.state_status !== 'needs_review' ||
			appliedDiscovery.current_stage !== 'discovery_report' ||
			appliedDiscovery.applied_count !== 0 ||
			!Array.isArray(appliedDiscovery.applied) ||
			appliedDiscovery.applied.length !== 0 ||
			appliedDiscovery.calibration_selection != null
		) {
			throw new Error('Сервер не подтвердил текущий вопрос о проекте');
		}
		const [run, questions] = await Promise.all([
			runStep((token) => getOnboardingBrandDiscovery(token, flow.discoveryRunId)),
			runStep((token) => getOnboardingBrandDiscoveryQuestions(token, flow.discoveryRunId))
		]);
		const pendingDiscoveryQuestion = projectScopedOnboardingPendingQuestion({
			scope: {
				stateId: flow.stateId,
				workspaceId: flow.workspaceId,
				brandId: flow.brandId,
				discoveryRunId: flow.discoveryRunId
			},
			run,
			questions: questions.items ?? []
		});
		if (!pendingDiscoveryQuestion) throw new Error('Сервер не подтвердил текущий вопрос о проекте');
		return createOnboarding1FlowState({
			...flow,
			stage: 'awaiting_discovery_question',
			brandReady: false,
			onboardingRevision: normalizeOnboardingRevision(appliedDiscovery.revision),
			brandVoiceReviewMessageId: '',
			pendingDiscoveryQuestion
		});
	}
	if (appliedDiscovery.calibration_selection?.selected_choice_ref !== choiceRef) {
		throw new Error('Сервер не подтвердил точный вариант Brand Voice');
	}

	// Both apply paths may initialize the same one-per-brand voice profile.
	// Keep them sequential and isolate enrichment failures: discovery establishes
	// the factual brand base first, then voice analysis enriches it when available.
	if (flow.brandVoiceRunId) {
		try {
			const voiceResult = await runStep((token) =>
				listBrandVoiceFindings(token, flow.brandId, flow.brandVoiceRunId)
			);
			const reviews = ((voiceResult?.items ?? []) as BrandVoiceFinding[])
				.filter((finding) => finding.status === 'pending')
				.map((finding) => {
					const safeCategories = flow.referenceScope
						? SCOPED_REFERENCE_SAFE_CATEGORIES
						: BRAND_VOICE_SAFE_CATEGORIES;
					const status: 'accepted' | 'rejected' = safeCategories.has(finding.category)
						? 'accepted'
						: 'rejected';
					return {
						finding_id: finding.id,
						status,
						edited_content: null
					};
				});
			if (reviews.length > 0) {
				await runStep((token) =>
					reviewBrandVoiceFindingsBatch(token, flow.brandId, flow.brandVoiceRunId, reviews)
				);
			}
			await runStep((token) =>
				applyBrandVoiceRun(token, flow.brandId, flow.brandVoiceRunId, {
					apply_memory_entries: true
				})
			);
		} catch (error) {
			if (isOnboardingUnauthorizedError(error)) throw error;
			console.warn('Brand Voice enrichment was skipped after Brand Memory was saved', error);
		}
	}
	return createOnboarding1FlowState({
		...flow,
		onboardingRevision: normalizeOnboardingRevision(appliedDiscovery.revision),
		brandReady: true,
		stage: [
			'write_posts_in_brand_voice',
			'create_post_series',
			'help_choose_first_step',
			'create_content_starter_kit'
		].includes(flow.intentId)
			? 'awaiting_topic_choice'
			: (flow.intentId === 'create_posts_from_sources' &&
						!flow.sourceContent.trim() &&
						flow.sourceIds.length === 0) ||
				  (flow.intentId === 'rewrite_text' && !flow.sourceContent.trim())
				? 'awaiting_source'
				: 'generating_result'
	});
};

/**
 * Brand Memory confirmation spans several independent API calls. Keep one
 * product-level deadline around the whole transaction so a disconnected or
 * half-open backend cannot leave the onboarding UI in an endless loading
 * state. All mutations are idempotent, therefore the existing recovery action
 * can safely retry the same step after a timeout.
 */
export const confirmOnboarding1BrandMemory = async (
	auth: OnboardingLiveAuthSource,
	flow: Onboarding1FlowState,
	options: Onboarding1MemoryConfirmOptions = {}
): Promise<Onboarding1FlowState> => {
	const timeoutMs = Math.max(1, options.timeoutMs ?? DEFAULT_ONBOARDING1_MEMORY_CONFIRM_TIMEOUT_MS);
	const maxAttempts = Math.max(1, options.maxAttempts ?? 2);
	const retryDelayMs = Math.max(0, options.retryDelayMs ?? 250);
	const confirmOperation = options.operation ?? confirmOnboarding1BrandMemoryOperation;
	const operation = async () => {
		let lastError: unknown;
		for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
			await options.onAttempt?.({ attempt, status: 'started' });
			try {
				const confirmed = await confirmOperation(auth, flow);
				await options.onAttempt?.({ attempt, status: 'succeeded' });
				return confirmed;
			} catch (error) {
				lastError = error;
				if (attempt >= maxAttempts || isNonRetryableOnboarding1MemoryConfirmError(error)) {
					await options.onAttempt?.({ attempt, status: 'failed', error });
					throw error;
				}
				await options.onAttempt?.({ attempt, status: 'retrying', error });
				await delayOnboarding1MemoryConfirmRetry(retryDelayMs);
			}
		}
		throw lastError;
	};
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeout = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(
			() => reject(new Error('Brand Memory confirmation timed out')),
			timeoutMs
		);
	});

	try {
		return await Promise.race([operation(), timeout]);
	} finally {
		if (timeoutId !== undefined) clearTimeout(timeoutId);
	}
};

const toText = (value: unknown, fallback = '') =>
	typeof value === 'string' && value.trim() ? value.trim() : fallback;

export const buildOnboarding1PostCardsPayload = (result: FirstDraftsResponse) => {
	const items = result?.preview?.items ?? [];
	const posts = items.map((item: Record<string, any>, index: number) => {
		const rawTitle = toText(item.title) || toText(item.hook);
		const title = /^(?:пост|post)\s+\d+$/i.test(rawTitle) ? '' : rawTitle;
		const body = toText(item.body_preview) || toText(item.body) || toText(item.summary);
		const cta = toText(item.cta);
		return {
			...item,
			cfPostId: toText(item.draft_id) || toText(item.id) || `onboarding-draft-${index + 1}`,
			versionId: toText(item.draft_version_id),
			platform: toText(item.platform, 'generic'),
			status: toText(item.status, 'Черновик'),
			action: 'Открыть',
			text: [title, body, cta].filter(Boolean).join('\n\n')
		};
	});
	return {
		type: 'post_cards_preview',
		origin: 'content_factory_generation_service',
		schema_version: 'content_factory_cards_v1',
		trusted: true,
		workspace_id: toText(result.metadata_json?.workspace_id, 'content-factory'),
		brand_id: toText(result.brand_id, toText(result.metadata_json?.brand_id, 'onboarding-brand')),
		generation_job_id: result.job_id ?? result.campaign_id ?? result.state_id,
		posts,
		preview: {
			items: posts
		}
	};
};

export const createOnboarding1Result = async (
	auth: OnboardingLiveAuthSource,
	flow: Onboarding1FlowState,
	topic = flow.topic,
	targetPlatforms: string[] = [],
	conversationId = ''
): Promise<{
	flow: Onboarding1FlowState;
	result: FirstDraftsResponse;
	content: string;
	contentFactoryAgent: Record<string, unknown>;
}> => {
	if (!flow.stateId) throw new Error('Не найдено состояние онбординга');
	const cleanTopic = topic.trim();
	const effectivePlatforms = resolveOnboarding1TargetPlatforms({
		prompt: cleanTopic,
		connectedPlatforms: targetPlatforms
	});
	const resultPlatforms = effectivePlatforms.length ? effectivePlatforms : ['generic'];
	const targetInstruction = effectivePlatforms.length
		? `Адаптируй результат под ${effectivePlatforms.join(', ')}.`
		: 'Подготовь универсальный черновик без неподтверждённой привязки к площадке.';
	const intentId = flow.intentId || 'write_posts_in_brand_voice';
	const sourceDependentScratchFallback =
		flow.sourceMode === 'scratch' &&
		['create_posts_from_sources', 'rewrite_text'].includes(intentId);
	let firstResultType: FirstResultType = 'custom';
	let variantCount = 1;
	let customRequest = '';
	let completionTitle = 'Пост готов и сохранён как черновик.';

	if (sourceDependentScratchFallback) {
		variantCount =
			intentId === 'create_posts_from_sources'
				? resolveOnboarding1RequestedCount({
						requestedCount: flow.requestedCount,
						prompt: cleanTopic,
						fallback: 3
					})
				: 1;
		customRequest = [
			`Подготовь ${onboarding1PostCountLabel(variantCount)} в стиле бренда на основе применённой Brand Memory.`,
			targetInstruction,
			`Тема и задача пользователя: ${cleanTopic}`,
			'Используй только подтверждённые факты из Brand Memory и задачи пользователя. Не выдавай описание бизнеса за внешний источник.',
			'Верни только готовые пользовательские черновики без технических пояснений. Не публикуй и не планируй автоматически.'
		].join('\\n');
		completionTitle =
			variantCount > 1
				? `Серия из ${variantCount} постов готова и сохранена в черновиках.`
				: 'Первый пост готов и сохранён как черновик.';
	} else if (intentId === 'create_post_series') {
		if (!cleanTopic) throw new Error('Напишите тему серии');
		variantCount = resolveOnboarding1RequestedCount({
			requestedCount: flow.requestedCount,
			prompt: cleanTopic,
			fallback: 3
		});
		customRequest = [
			`Создай серию из ${onboarding1PostCountLabel(variantCount)} по применённому Brand Voice.`,
			targetInstruction,
			`Общая тема серии: ${cleanTopic}`,
			'Каждый пост должен раскрывать отдельный угол темы, не повторять предыдущий и быть готовым черновиком.',
			'Начинай каждую карточку сразу с полноценного текста. Не добавляй служебные подписи или нумерацию «Пост 1», «Пост 2».',
			'Не добавляй технических пояснений, не публикуй и не планируй автоматически.'
		].join('\n');
		completionTitle = `Серия из ${variantCount} постов готова и сохранена в черновиках.`;
	} else if (intentId === 'create_posts_from_sources') {
		firstResultType = 'source_repurpose';
		variantCount = resolveOnboarding1RequestedCount({
			requestedCount: flow.requestedCount,
			prompt: flow.sourceContent,
			fallback: 3
		});
		if (!flow.sourceContent.trim() && flow.sourceIds.length === 0) {
			throw new Error('Добавьте материал для переработки');
		}
		customRequest = [
			`Подготовь ${onboarding1PostCountLabel(variantCount)} на основе добавленных материалов.`,
			targetInstruction,
			'Сохрани факты и смысл исходника, но не копируй его дословно. Для каждого поста выбери отдельный полезный угол.',
			'Начинай каждую карточку сразу с полноценного текста. Не добавляй служебные подписи или нумерацию «Пост 1», «Пост 2».',
			'Не выдумывай факты вне материалов. Не публикуй и не планируй автоматически.'
		].join('\n');
		completionTitle = `Материал превращён в ${variantCount} готовых черновика.`;
	} else if (intentId === 'rewrite_text') {
		if (!flow.sourceContent.trim()) throw new Error('Вставьте текст для переработки');
		customRequest = [
			'Переработай текст пользователя в один готовый пост.',
			targetInstruction,
			'Сделай его яснее, живее и сильнее, сохрани исходные факты и смысл, примени Brand Voice без лишней официальности.',
			`Исходный текст:\n${flow.sourceContent.trim()}`,
			'Верни только готовый пользовательский черновик. Не публикуй и не планируй автоматически.'
		].join('\n');
		completionTitle = 'Текст переработан и сохранён как черновик.';
	} else {
		if (!cleanTopic) throw new Error('Напишите тему первого поста');
		customRequest = [
			'Создай один готовый пост по применённому Brand Voice из Brand Memory.',
			targetInstruction,
			`Тема и задача пользователя: ${cleanTopic}`,
			'Верни только пользовательский черновик без технических пояснений. Не публикуй и не планируй автоматически.'
		].join('\n');
		completionTitle = 'Первый пост готов и сохранён как черновик.';
	}

	const clarification = readOnboardingFirstResultClarification(
		flow.firstResultClarification,
		flow.stateId
	);
	if (clarification?.status === 'answered') {
		customRequest += `\nДополнительные факты пользователя о продукте или проекте: ${clarification.answer}`;
	}
	const voiceGuidance = flow.brandVoiceGuidance.map((item) => item.trim()).filter(Boolean);
	if (voiceGuidance.length > 0) {
		customRequest += `\nДополнительная правка подачи от пользователя: ${voiceGuidance.join('\n')}`;
	}
	const getToken = toOnboardingLiveAuthSupplier(auth);
	const runStep = <Result>(request: (token: string) => Promise<Result>) =>
		runOnboardingLiveAuthStep(getToken, request);
	const resultKeys = buildOnboarding1FirstResultIdempotencyKeys({
		stateId: flow.stateId,
		firstResultType,
		customRequest,
		platforms: resultPlatforms,
		variantCount,
		selectedCalibrationId: flow.selectedCalibrationId,
		selectedCalibrationChoiceRef: flow.selectedCalibrationChoiceRef,
		calibrationChoiceSetRef: flow.summary?.calibrationChoiceSetRef ?? '',
		brandVoiceRunId: flow.brandVoiceRunId,
		attempt: flow.firstResultAttempt,
		expectedRevision: flow.onboardingRevision
	});
	let selectedRevision = normalizeOnboardingRevision(flow.onboardingRevision);
	if (flow.firstResultAttempt <= 1) {
		const selected = await runStep(async (token) => {
			try {
				return await submitOnboardingFirstResultSelection({
					storage: typeof localStorage !== 'undefined' ? localStorage : undefined,
					intent: {
						state_id: flow.stateId,
						first_result_type: firstResultType,
						custom_request: customRequest
					},
					snapshotRevision: flow.onboardingRevision,
					idempotencyKey: resultKeys.select,
					replayScope: `onboarding1-attempt-${flow.firstResultAttempt}`,
					send: (body) => selectOnboardingFirstResult(token, body)
				});
			} catch (error) {
				if (!isOnboardingFirstResultRevisionConflict(error)) throw error;
				const latest = await getOnboardingState(token, flow.stateId).catch(() => null);
				throw new Onboarding1FirstResultRevisionConflictError(
					normalizeOnboardingRevision(latest?.revision)
				);
			}
		});
		selectedRevision = normalizeOnboardingRevision(selected.revision) ?? selectedRevision;
	}
	const result = await runStep((token) =>
		createOnboardingFirstDrafts(token, {
			state_id: flow.stateId,
			...(conversationId.trim() ? { conversation_id: conversationId.trim() } : {}),
			first_result_type: firstResultType,
			custom_request: customRequest,
			platforms: resultPlatforms,
			variant_count: variantCount,
			idempotency_key: resultKeys.create
		})
	);
	if (result.generation_source === 'failed' || !(result.preview?.items?.length ?? 0)) {
		throw new Onboarding1ExplicitResultFailureError(
			String((result.preview as Record<string, any>)?.message ?? 'Результат не был создан'),
			flow.firstResultAttempt,
			selectedRevision,
			result
		);
	}
	const completed = await runStep((token) =>
		completeOnboarding(token, {
			state_id: flow.stateId,
			idempotency_key: resultKeys.complete
		})
	);
	const payload = buildOnboarding1PostCardsPayload(result);
	const content = [
		`${completionTitle} Я ничего не публиковал.`,
		'',
		'Я запомнил факты, аудиторию и подачу проекта — в следующих задачах заново объяснять их не придётся.'
	].join('\n');
	return {
		flow: createOnboarding1FlowState({
			...flow,
			onboardingRevision: normalizeOnboardingRevision(completed?.revision) ?? selectedRevision,
			stage: 'completed',
			topic: cleanTopic
		}),
		result,
		content,
		contentFactoryAgent: {
			type: 'agent_execution_result',
			execution_status: 'completed',
			intent: variantCount > 1 ? 'generate_posts' : 'generate_post',
			route: 'content_factory.onboarding_first_result',
			content_factory_ui: {
				state: 'style_engine_preview_ready'
			},
			render: payload
		}
	};
};

export const createOnboarding1FirstPost = async (
	auth: OnboardingLiveAuthSource,
	flow: Onboarding1FlowState,
	topic: string,
	targetPlatforms: string[] = [],
	conversationId = ''
) => await createOnboarding1Result(auth, flow, topic, targetPlatforms, conversationId);
